/**
 * Journal bounded context.
 *
 * Journal is the only place in Fighting Form where the user's own prose lives,
 * so this file carries two obligations the rest of the domain does not:
 *
 *  1. Byte determinism. Entries are content-hashed and every write is verified
 *     by re-reading and comparing canonical encodings, so the shapes declared
 *     here double as the on-disk wire shapes. Field names deliberately mirror
 *     the Swift record keys (`sessionID`, `ownerEntryID`, `contentSHA256`,
 *     `durationMilliseconds`) rather than being re-spelled to TypeScript taste
 *     — a rename would silently change the canonical bytes and invalidate every
 *     stored hash.
 *  2. Structural concealment. Journal Lock is not a view-layer curtain. A
 *     locked vault does not hold the entries behind an `isVisible` flag; the
 *     locked variants of `JournalVault` have no field in which entry content
 *     could sit. Anything that can reach a projection has already been through
 *     the lock.
 *
 * Everything in this file is pure. Authentication, file protection, staging,
 * and playback are ports supplied by @ff/store and @ff/web.
 */

import type { Milliseconds } from "./time";
import { spokenDuration } from "./time";
// The recap snapshot joins back to a canonical effort session by id only. The
// snapshot itself is a frozen copy of text (see JournalEffortSnapshot) so the
// dependency is one branded identifier and nothing else.
import type { EffortSessionId } from "./effort";

// ---------------------------------------------------------------------------
// Identity
// ---------------------------------------------------------------------------

declare const journalBrand: unique symbol;

/** Nominal typing helper. Local to this file so sibling contexts stay independent. */
type JournalBranded<T, B extends string> = T & { readonly [journalBrand]: B };

export type JournalEntryId = JournalBranded<string, "JournalEntryId">;

/**
 * A draft is stored under the id of the entry it shadows (`draft.id ===
 * draft.entry.id` is a repository invariant), which is what makes the
 * draft-overlay projection a same-id replacement rather than a join. Aliasing
 * the type — instead of minting a second brand — makes that invariant hold at
 * compile time.
 */
export type JournalDraftId = JournalEntryId;

export type JournalBlockId = JournalBranded<string, "JournalBlockId">;
export type JournalAttachmentId = JournalBranded<string, "JournalAttachmentId">;
export type JournalGoalId = JournalBranded<string, "JournalGoalId">;
export type JournalPromptId = JournalBranded<string, "JournalPromptId">;

/**
 * Handle for a file sitting in the caches staging area during import or voice
 * recording. It is deliberately opaque here: the domain must never learn a
 * filesystem path, because path handling is where cleanup turns into arbitrary
 * deletion (F-JE-22, F-JP-31).
 */
export type JournalStagedMediaId = JournalBranded<string, "JournalStagedMediaId">;

/**
 * Composite row key for the recovery list: `"kind|standardized path|sha256"`.
 * An entry, a draft, and a goal can all share one logical UUID; keying rows by
 * that UUID would merge three distinct preserved files into one row and hide
 * two of them from recovery.
 */
export type JournalRecoveryPresentationId = JournalBranded<
  string,
  "JournalRecoveryPresentationId"
>;

/** 64 lowercase hex characters. */
export type Sha256Hex = JournalBranded<string, "Sha256Hex">;

const SHA256_HEX_PATTERN = /^[0-9a-f]{64}$/;

export function isSha256Hex(value: string): value is Sha256Hex {
  return SHA256_HEX_PATTERN.test(value);
}

/** Revisions saturate rather than overflow, so they are their own type. */
export type JournalRevision = JournalBranded<number, "JournalRevision">;

/**
 * Swift saturates at `Int.max`; the web port saturates at the largest integer
 * that survives a JSON round trip. Beyond this a revision would silently
 * compare equal to its predecessor and the revision-conflict check — the only
 * guard against a stale composer overwriting a newer entry — would stop firing.
 */
export const JOURNAL_REVISION_MAX = Number.MAX_SAFE_INTEGER as JournalRevision;

export const JOURNAL_FIRST_REVISION = 1 as JournalRevision;

export function nextJournalRevision(current: JournalRevision): JournalRevision {
  return (current >= JOURNAL_REVISION_MAX
    ? JOURNAL_REVISION_MAX
    : current + 1) as JournalRevision;
}

// ---------------------------------------------------------------------------
// Canonical time
// ---------------------------------------------------------------------------

/**
 * Epoch microseconds. Journal dates are not milliseconds: draft `savedAt` must
 * strictly increase by at least one microsecond between autosaves, which is the
 * mechanism that lets two saves inside the same millisecond still order
 * deterministically. Storing them as milliseconds would make consecutive
 * autosaves compare equal and the newest draft would become ambiguous.
 */
export type JournalInstant = JournalBranded<number, "JournalInstant">;

export const JOURNAL_MICROSECONDS_PER_MILLISECOND = 1_000;

/**
 * Canonical encoding writes a single integer, so the representable range is the
 * range in which a JavaScript number is still an exact integer. Anything wider
 * would encode and decode to different bytes and fail the write-verification
 * comparison.
 */
export const JOURNAL_INSTANT_MIN = -Number.MAX_SAFE_INTEGER as JournalInstant;
export const JOURNAL_INSTANT_MAX = Number.MAX_SAFE_INTEGER as JournalInstant;

export const JOURNAL_CANONICAL_DATE_RANGE_ERROR =
  "Date is outside canonical epoch-microsecond range";

export function isJournalInstant(value: number): value is JournalInstant {
  return (
    Number.isInteger(value) &&
    value >= JOURNAL_INSTANT_MIN &&
    value <= JOURNAL_INSTANT_MAX
  );
}

/**
 * Rounds half away from zero, matching the Swift encoder. In-memory values are
 * pre-rounded through this so an entry compared against its own re-decoded copy
 * does not differ by a sub-microsecond fraction that never survived encoding.
 */
export function canonicalizeJournalInstant(epochMicroseconds: number): JournalInstant {
  const rounded =
    epochMicroseconds < 0
      ? -Math.round(-epochMicroseconds)
      : Math.round(epochMicroseconds);
  if (!isJournalInstant(rounded)) {
    throw new RangeError(JOURNAL_CANONICAL_DATE_RANGE_ERROR);
  }
  return rounded;
}

export function journalInstantFromMilliseconds(epochMilliseconds: number): JournalInstant {
  return canonicalizeJournalInstant(
    epochMilliseconds * JOURNAL_MICROSECONDS_PER_MILLISECOND,
  );
}

export function journalInstantToMilliseconds(instant: JournalInstant): number {
  return instant / JOURNAL_MICROSECONDS_PER_MILLISECOND;
}

/**
 * Every draft write stamps `max(now, lastSaved + 1µs)`. Without the forced
 * advance, a clock that does not move — or moves backwards across a system time
 * change — would let an older draft win the "newest savedAt" comparison and
 * resurrect text the user already replaced.
 */
export function nextJournalSavedAt(
  now: JournalInstant,
  lastSavedAt: JournalInstant | null,
): JournalInstant {
  if (lastSavedAt === null) return now;
  return Math.max(now, lastSavedAt + 1) as JournalInstant;
}

// ---------------------------------------------------------------------------
// Schema versions and the migration hook
// ---------------------------------------------------------------------------

export const JOURNAL_DOCUMENT_SCHEMA_VERSION = 1;
export const JOURNAL_ENTRY_SCHEMA_VERSION = 2;
export const JOURNAL_DRAFT_SCHEMA_VERSION = 1;
export const JOURNAL_GOAL_SCHEMA_VERSION = 1;
export const JOURNAL_ATTACHMENT_SCHEMA_VERSION = 2;
export const JOURNAL_DELETION_MANIFEST_SCHEMA_VERSION = 1;

/**
 * Every record file starts with a decodable header so a future-schema file can
 * be recognised and quarantined *before* the body is parsed against a shape it
 * was never written for. Reading the header must never fail on unknown fields.
 */
export interface JournalSchemaHeader {
  readonly schemaVersion: number;
  /** Absent when the file's bytes are damaged before the id field. */
  readonly id?: string;
}

export type JournalRecordType = "entry" | "draft" | "goal";

export const JOURNAL_SUPPORTED_SCHEMA_VERSIONS: Readonly<
  Record<JournalRecordType, number>
> = Object.freeze({
  entry: JOURNAL_ENTRY_SCHEMA_VERSION,
  draft: JOURNAL_DRAFT_SCHEMA_VERSION,
  goal: JOURNAL_GOAL_SCHEMA_VERSION,
});

/**
 * A single upward step. Migrations are registered rather than branched inline
 * so that adding schema 2 of the document format cannot accidentally change how
 * schema 1 decodes — the existing step keeps its own frozen implementation.
 */
export interface JournalDocumentMigration {
  readonly from: number;
  readonly to: number;
  /**
   * Receives the raw decoded JSON of the older document, not a typed value:
   * the older shape no longer has a type in this file once it is superseded.
   */
  readonly migrate: (raw: unknown) => JournalDocument;
}

export type JournalDocumentMigrationRegistry = readonly JournalDocumentMigration[];

/**
 * Schema 1 is the original format, so the registry ships empty. It exists now
 * because the decision that matters — that unsupported versions quarantine
 * instead of being coerced — has to be encoded before there is a second
 * version to coerce.
 */
export const JOURNAL_DOCUMENT_MIGRATIONS: JournalDocumentMigrationRegistry =
  Object.freeze([]);

export type JournalMigrationPlan =
  | { readonly kind: "current" }
  | { readonly kind: "upgrade"; readonly steps: readonly JournalDocumentMigration[] }
  /** Written by a newer build. Preserve the bytes; never guess at the shape. */
  | { readonly kind: "futureSchema"; readonly found: number; readonly supported: number }
  /** Older than anything we still know how to read. Also a quarantine case. */
  | { readonly kind: "unsupported"; readonly found: number };

/**
 * Resolving a migration path is a pure lookup so that the "can this file be
 * read at all" decision is testable without touching storage, and so an
 * incomplete chain fails as `unsupported` rather than half-migrating.
 */
export function planJournalDocumentMigration(
  found: number,
  registry: JournalDocumentMigrationRegistry = JOURNAL_DOCUMENT_MIGRATIONS,
): JournalMigrationPlan {
  const supported = JOURNAL_DOCUMENT_SCHEMA_VERSION;
  if (found === supported) return { kind: "current" };
  if (found > supported) return { kind: "futureSchema", found, supported };

  const steps: JournalDocumentMigration[] = [];
  let version = found;
  while (version < supported) {
    const step = registry.find((candidate) => candidate.from === version);
    if (step === undefined) return { kind: "unsupported", found };
    steps.push(step);
    version = step.to;
  }
  return { kind: "upgrade", steps };
}

// ---------------------------------------------------------------------------
// Rich text: marks
// ---------------------------------------------------------------------------

export type JournalMarkKind =
  | "strong"
  | "emphasis"
  | "underline"
  | "strikethrough"
  | "link";

/**
 * Canonical order is not cosmetic. Markdown is emitted by walking the marks in
 * reverse, so this ordering is what fixes outer-to-inner nesting; two runs with
 * the same marks in different orders would export to different bytes and hash
 * differently.
 */
export const JOURNAL_MARK_ORDER: Readonly<Record<JournalMarkKind, number>> =
  Object.freeze({
    strong: 0,
    emphasis: 1,
    underline: 2,
    strikethrough: 3,
    link: 4,
  });

/**
 * `link` carries a destination; every other kind must carry none. Modelling it
 * as a union removes the "underline with a href" state that validation would
 * otherwise have to reject at runtime.
 */
export type JournalTextMark =
  | { readonly kind: Exclude<JournalMarkKind, "link">; readonly value?: undefined }
  | { readonly kind: "link"; readonly value: string };

/** `toggle(.link)` is a deliberate no-op — links are set only through setLink. */
export type JournalToggleableMarkKind = Exclude<JournalMarkKind, "link">;

export const JOURNAL_TOGGLEABLE_MARKS: readonly JournalToggleableMarkKind[] =
  Object.freeze(["strong", "emphasis", "underline", "strikethrough"] as const);

function compareMarks(a: JournalTextMark, b: JournalTextMark): number {
  const orderDelta = JOURNAL_MARK_ORDER[a.kind] - JOURNAL_MARK_ORDER[b.kind];
  if (orderDelta !== 0) return orderDelta;
  const left = a.value ?? "";
  const right = b.value ?? "";
  return left < right ? -1 : left > right ? 1 : 0;
}

/**
 * Sorts, drops non-link destinations, deduplicates, and runs every link
 * destination through {@link sanitizeJournalLinkDestination}.
 *
 * The sanitizer belongs *here* rather than at a second enforcement point: the
 * repository's gate is "reject non-canonical mark arrays", so canonical has to
 * imply safe or `javascript:` survives the gate and is emitted straight into the
 * exported Markdown. A destination that cannot be made safe drops the mark
 * entirely, degrading the run to plain text — the documented behavior — instead
 * of encoding a link nothing may follow.
 */
export function canonicalizeJournalMarks(
  marks: readonly JournalTextMark[],
): readonly JournalTextMark[] {
  const normalized: JournalTextMark[] = [];
  for (const mark of marks) {
    if (mark.kind === "link") {
      // A link mark with no usable — or no safe — destination is not a link at
      // all; keeping it would encode a mark whose Markdown form is `[text]()`,
      // or worse, one whose destination is a script URL.
      const value = sanitizeJournalLinkDestination(mark.value);
      if (value === null) continue;
      normalized.push({ kind: "link", value });
    } else {
      normalized.push({ kind: mark.kind });
    }
  }
  normalized.sort(compareMarks);

  const deduplicated: JournalTextMark[] = [];
  for (const mark of normalized) {
    const previous = deduplicated[deduplicated.length - 1];
    if (previous !== undefined && previous.kind === mark.kind) {
      if (previous.kind !== "link" || previous.value === mark.value) continue;
    }
    deduplicated.push(mark);
  }
  return deduplicated;
}

export function areJournalMarksCanonical(
  marks: readonly JournalTextMark[],
): boolean {
  const canonical = canonicalizeJournalMarks(marks);
  if (canonical.length !== marks.length) return false;
  return marks.every((mark, index) => {
    const expected = canonical[index];
    return (
      expected !== undefined &&
      expected.kind === mark.kind &&
      (expected.value ?? null) === (mark.value ?? null)
    );
  });
}

// ---------------------------------------------------------------------------
// Rich text: link sanitizer (the XSS boundary)
// ---------------------------------------------------------------------------

export const JOURNAL_ALLOWED_LINK_SCHEMES: readonly string[] = Object.freeze([
  "http",
  "https",
  "mailto",
]);

// eslint-disable-next-line no-control-regex
const CONTROL_CHARACTERS = /[\u0000-\u001f\u007f]/;

/**
 * The single boundary that keeps `javascript:`, `data:`, `file:` and custom
 * schemes out of the document. It runs on encode *and* on decode, so a link
 * that became unsafe after storage — or that was written by a tampered file —
 * disappears from the projection instead of being rendered once.
 *
 * Returns the sanitized destination, or null when the input cannot be made
 * safe. Never throws: a bad link must degrade to plain text, not break the
 * whole entry.
 */
export function sanitizeJournalLinkDestination(raw: string): string | null {
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  // A control character can split a URL for one parser and not another, which
  // is exactly how scheme checks get bypassed.
  if (CONTROL_CHARACTERS.test(trimmed)) return null;

  let candidate = trimmed;
  const lowered = trimmed.toLowerCase();
  if (!trimmed.includes("://") && !lowered.startsWith("mailto:")) {
    candidate =
      trimmed.includes("@") && !trimmed.includes("/")
        ? `mailto:${trimmed}`
        : `https://${trimmed}`;
  }

  let parsed: URL;
  try {
    parsed = new URL(candidate);
  } catch {
    return null;
  }

  const scheme = parsed.protocol.replace(/:$/, "").toLowerCase();
  if (!JOURNAL_ALLOWED_LINK_SCHEMES.includes(scheme)) return null;

  if (scheme === "mailto") {
    if (parsed.pathname.length === 0 || !parsed.pathname.includes("@")) return null;
    return candidate;
  }
  if (parsed.hostname.length === 0) return null;
  return parsed.toString();
}

// ---------------------------------------------------------------------------
// Rich text: blocks
// ---------------------------------------------------------------------------

/** Exactly eight, in this order. The order is the menu order and the raw order. */
export type JournalBlockStyle =
  | "paragraph"
  | "heading1"
  | "heading2"
  | "heading3"
  | "unorderedListItem"
  | "orderedListItem"
  | "checklistItem"
  | "quote";

export const JOURNAL_BLOCK_STYLES: readonly JournalBlockStyle[] = Object.freeze([
  "paragraph",
  "heading1",
  "heading2",
  "heading3",
  "unorderedListItem",
  "orderedListItem",
  "checklistItem",
  "quote",
] as const);

export type JournalHeadingStyle = "heading1" | "heading2" | "heading3";
export type JournalListStyle =
  | "unorderedListItem"
  | "orderedListItem"
  | "checklistItem";

export type JournalNestingLevel = 0 | 1 | 2 | 3 | 4 | 5 | 6;

export const JOURNAL_MAX_NESTING_LEVEL = 6;

export function isJournalListStyle(style: JournalBlockStyle): style is JournalListStyle {
  return (
    style === "unorderedListItem" ||
    style === "orderedListItem" ||
    style === "checklistItem"
  );
}

export function isJournalHeadingStyle(
  style: JournalBlockStyle,
): style is JournalHeadingStyle {
  return style === "heading1" || style === "heading2" || style === "heading3";
}

/** Only list styles nest; a nested quote or heading has no rendering. */
export function normalizeJournalNestingLevel(
  style: JournalBlockStyle,
  raw: number,
): JournalNestingLevel {
  if (!isJournalListStyle(style)) return 0;
  const clamped = Math.max(0, Math.min(JOURNAL_MAX_NESTING_LEVEL, Math.trunc(raw)));
  return clamped as JournalNestingLevel;
}

export interface JournalTextRun {
  readonly text: string;
  /** Canonical order, deduplicated. See canonicalizeJournalMarks. */
  readonly marks: readonly JournalTextMark[];
}

interface JournalTextBlockShape {
  readonly kind: "text";
  readonly id: JournalBlockId;
  readonly runs: readonly JournalTextRun[];
}

/**
 * `isChecked` is present if and only if the style is `checklistItem`, and
 * nesting is zero unless the style is a list style. Splitting the union on
 * style makes both invariants compile-time facts instead of validation rules
 * that a codec could forget to apply.
 */
export type JournalTextBlock =
  | (JournalTextBlockShape & {
      readonly style: "paragraph" | "heading1" | "heading2" | "heading3" | "quote";
      readonly nestingLevel: 0;
      readonly isChecked?: undefined;
    })
  | (JournalTextBlockShape & {
      readonly style: "unorderedListItem" | "orderedListItem";
      readonly nestingLevel: JournalNestingLevel;
      readonly isChecked?: undefined;
    })
  | (JournalTextBlockShape & {
      readonly style: "checklistItem";
      readonly nestingLevel: JournalNestingLevel;
      readonly isChecked: boolean;
    });

/**
 * The embed types the editor never sees. The rich editor edits a text-only
 * projection; embeds are re-interleaved positionally at save time, which is why
 * they are a separate block kind rather than a run mark.
 */
export interface JournalMediaEmbed {
  readonly attachmentId: JournalAttachmentId;
}

export type JournalEmbed =
  | { readonly kind: "effort"; readonly effort: JournalEffortLink }
  | { readonly kind: "media"; readonly media: JournalMediaEmbed }
  | { readonly kind: "prompt"; readonly prompt: JournalPromptReference };

export interface JournalEmbedBlock {
  readonly kind: "embed";
  readonly id: JournalBlockId;
  readonly embed: JournalEmbed;
}

/** Externally tagged: every persisted block is `{"kind":"text"|"embed", …}`. */
export type JournalBlock = JournalTextBlock | JournalEmbedBlock;

export interface JournalDocument {
  readonly schemaVersion: typeof JOURNAL_DOCUMENT_SCHEMA_VERSION;
  readonly blocks: readonly JournalBlock[];
}

export const JOURNAL_EMPTY_DOCUMENT: JournalDocument = Object.freeze({
  schemaVersion: JOURNAL_DOCUMENT_SCHEMA_VERSION,
  blocks: Object.freeze([]) as readonly JournalBlock[],
});

/**
 * The permissive shape a lossless decoder produces before validation. Decoding
 * deliberately does not repair anything: a malformed persisted block must reach
 * the quarantine lane with its bytes intact, because silently normalising it
 * would destroy the only copy of whatever the user actually wrote.
 */
export interface JournalEncodedBlock {
  readonly kind: string;
  readonly id?: string;
  readonly style?: string;
  readonly nestingLevel?: number;
  readonly isChecked?: boolean;
  readonly runs?: readonly {
    readonly text?: string;
    readonly marks?: readonly { readonly kind?: string; readonly value?: string }[];
  }[];
  readonly embed?: unknown;
}

export interface JournalEncodedDocument {
  readonly schemaVersion: number;
  readonly blocks: readonly JournalEncodedBlock[];
}

export function isJournalTextBlock(block: JournalBlock): block is JournalTextBlock {
  return block.kind === "text";
}

export function isJournalEmbedBlock(block: JournalBlock): block is JournalEmbedBlock {
  return block.kind === "embed";
}

// ---------------------------------------------------------------------------
// Rich text: derived text
// ---------------------------------------------------------------------------

export function journalRunPlainText(runs: readonly JournalTextRun[]): string {
  return runs.map((run) => run.text).join("");
}

/** Blocks join with a single newline; embeds contribute nothing to plain text. */
export function journalDocumentPlainText(document: JournalDocument): string {
  return document.blocks
    .filter(isJournalTextBlock)
    .map((block) => journalRunPlainText(block.runs))
    .join("\n");
}

const MARKDOWN_ESCAPED_CHARACTERS = new Set([
  "\\",
  "`",
  "*",
  "_",
  "{",
  "}",
  "[",
  "]",
  "<",
  ">",
  "(",
  ")",
  "#",
  "+",
  "-",
  ".",
  "!",
  "|",
  "~",
]);

export function escapeJournalMarkdownText(text: string): string {
  let escaped = "";
  for (const character of text) {
    escaped += MARKDOWN_ESCAPED_CHARACTERS.has(character)
      ? `\\${character}`
      : character;
  }
  return escaped;
}

/**
 * Link destinations use a narrower escape set than body text: escaping `*` or
 * `_` inside a URL would change the address rather than protect it.
 */
export function escapeJournalMarkdownLinkDestination(destination: string): string {
  return destination
    .replace(/\\/g, "\\\\")
    .replace(/\(/g, "\\(")
    .replace(/\)/g, "\\)")
    .replace(/ /g, "%20");
}

function markdownForRun(run: JournalTextRun): string {
  let rendered = escapeJournalMarkdownText(run.text);
  // Reversed, so canonical order becomes outermost-last wrapping.
  for (let index = run.marks.length - 1; index >= 0; index -= 1) {
    const mark = run.marks[index];
    if (mark === undefined) continue;
    switch (mark.kind) {
      case "strong":
        rendered = `**${rendered}**`;
        break;
      case "emphasis":
        rendered = `*${rendered}*`;
        break;
      case "underline":
        rendered = `<u>${rendered}</u>`;
        break;
      case "strikethrough":
        rendered = `~~${rendered}~~`;
        break;
      case "link":
        rendered = `[${rendered}](${escapeJournalMarkdownLinkDestination(mark.value)})`;
        break;
    }
  }
  return rendered;
}

function markdownIndent(level: JournalNestingLevel): string {
  return "  ".repeat(level);
}

export function journalBlockMarkdown(block: JournalBlock): string {
  if (block.kind === "embed") {
    switch (block.embed.kind) {
      case "effort": {
        const snapshot = block.embed.effort.snapshot;
        return `> Workout: ${snapshot.activityName} · ${journalEffortDurationText(
          snapshot.activeDurationMilliseconds,
        )}`;
      }
      case "media":
        return `[Media attachment: ${block.embed.media.attachmentId.toLowerCase()}]`;
      case "prompt":
        return `> Prompt: ${block.embed.prompt.text}`;
    }
  }

  const content = block.runs.map(markdownForRun).join("");
  switch (block.style) {
    case "paragraph":
      return content;
    case "heading1":
      return `# ${content}`;
    case "heading2":
      return `## ${content}`;
    case "heading3":
      return `### ${content}`;
    case "unorderedListItem":
      return `${markdownIndent(block.nestingLevel)}- ${content}`;
    case "orderedListItem":
      return `${markdownIndent(block.nestingLevel)}1. ${content}`;
    case "checklistItem":
      return `${markdownIndent(block.nestingLevel)}- [${
        block.isChecked ? "x" : " "
      }] ${content}`;
    case "quote":
      // Every line, not just the first: a soft-wrapped quote that loses its
      // marker on continuation lines stops being a quote on re-import.
      return content
        .split("\n")
        .map((line) => `> ${line}`)
        .join("\n");
  }
}

/** Markdown joins blocks with a blank line; plain text joins with one newline. */
export function journalDocumentMarkdown(document: JournalDocument): string {
  return document.blocks.map(journalBlockMarkdown).join("\n\n");
}

export function journalEntryMarkdown(entry: JournalEntry): string {
  const body = journalDocumentMarkdown(entry.document);
  const title = entry.title?.trim();
  if (title === undefined || title.length === 0) return body;
  return `# ${escapeJournalMarkdownText(title)}\n\n${body}`;
}

/** `h:mm:ss` at or above an hour, `m:ss` below it — the embed card's duration form. */
export function journalEffortDurationText(duration: Milliseconds): string {
  const totalSeconds = Math.max(0, Math.round(duration / 1_000));
  const hours = Math.floor(totalSeconds / 3_600);
  const minutes = Math.floor((totalSeconds % 3_600) / 60);
  const seconds = totalSeconds % 60;
  const paddedSeconds = String(seconds).padStart(2, "0");
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, "0")}:${paddedSeconds}`;
  }
  return `${minutes}:${paddedSeconds}`;
}

/**
 * Ordered-list numbering walks backwards over contiguous prior ordered items,
 * stopping at a shallower level and incrementing only at an equal level. It is
 * computed rather than stored so that indenting an item renumbers its siblings
 * without rewriting every following block.
 */
export function journalOrderedListNumber(
  blocks: readonly JournalBlock[],
  index: number,
): number {
  const target = blocks[index];
  if (
    target === undefined ||
    target.kind !== "text" ||
    target.style !== "orderedListItem"
  ) {
    return 1;
  }
  let number = 1;
  for (let cursor = index - 1; cursor >= 0; cursor -= 1) {
    const block = blocks[cursor];
    if (block === undefined || block.kind !== "text") break;
    if (block.style !== "orderedListItem") break;
    if (block.nestingLevel < target.nestingLevel) break;
    if (block.nestingLevel === target.nestingLevel) number += 1;
  }
  return number;
}

export type JournalUnorderedMarker = "disc" | "circle";

/**
 * Alternating markers must be produced explicitly: browsers give `list-style`
 * no equivalent for the checklist style, so the whole marker column is drawn by
 * the app and the alternation would otherwise silently vanish.
 */
export function journalUnorderedMarker(level: JournalNestingLevel): JournalUnorderedMarker {
  return level % 2 === 0 ? "disc" : "circle";
}

// ---------------------------------------------------------------------------
// Rich text: editor projection contract
// ---------------------------------------------------------------------------

/**
 * What a selection reports. `null` means "not uniform across the selection",
 * which the style button renders as the literal word "Mixed" — distinct from a
 * uniform style that happens to be paragraph.
 */
export interface JournalEditorSelectionState {
  readonly blockStyle: JournalBlockStyle | null;
  readonly isStrong: boolean;
  readonly isEmphasized: boolean;
  readonly isUnderlined: boolean;
  readonly isStruckThrough: boolean;
  readonly linkDestination: string | null;
  readonly isChecklistChecked: boolean | null;
}

export const JOURNAL_MIXED_STYLE_LABEL = "Mixed";

export const JOURNAL_EMPTY_SELECTION_STATE: JournalEditorSelectionState =
  Object.freeze({
    blockStyle: "paragraph" as JournalBlockStyle,
    isStrong: false,
    isEmphasized: false,
    isUnderlined: false,
    isStruckThrough: false,
    linkDestination: null,
    isChecklistChecked: null,
  });

/**
 * The controller exposes commands, never an attributed string. Keeping the
 * document as the only writable representation is what stops a paste from
 * smuggling fonts, colours, or attachments into persisted truth.
 */
export type JournalEditorCommand =
  | { readonly kind: "focus" }
  | { readonly kind: "dismissKeyboard" }
  | { readonly kind: "undo" }
  | { readonly kind: "redo" }
  | { readonly kind: "setBlockStyle"; readonly style: JournalBlockStyle }
  | { readonly kind: "toggleMark"; readonly mark: JournalToggleableMarkKind }
  /** null or whitespace removes the link and always succeeds. */
  | { readonly kind: "setLink"; readonly destination: string | null }
  | { readonly kind: "toggleChecklistState" }
  | { readonly kind: "indentList" }
  | { readonly kind: "outdentList" };

export const JOURNAL_UNDO_ACTION_NAMES = Object.freeze({
  changeStyle: "Change Style",
  strong: "Bold",
  emphasis: "Italic",
  underline: "Underline",
  strikethrough: "Strikethrough",
  addLink: "Add Link",
  removeLink: "Remove Link",
  completeItem: "Complete Item",
  reopenItem: "Reopen Item",
  indent: "Indent",
  outdent: "Outdent",
} as const);

/** List styles and quote collapse to a paragraph when Return is hit while empty. */
export function journalStyleExitsWhenEmpty(style: JournalBlockStyle): boolean {
  return isJournalListStyle(style) || style === "quote";
}

/**
 * Headings do not continue onto the next line — a heading followed by Return is
 * almost always the start of body text, and continuing it forces the user to
 * undo the style on every paragraph.
 */
export function journalStyleAfterReturn(style: JournalBlockStyle): JournalBlockStyle {
  return isJournalHeadingStyle(style) ? "paragraph" : style;
}

/**
 * The object-replacement character marks an inline attachment in the platform
 * text system. Any replacement longer than one character, or containing it,
 * forces a full sanitize pass — which is why paste and attachment insertion can
 * never leave unsanitized content in the document.
 */
export const JOURNAL_OBJECT_REPLACEMENT_CHARACTER = "\u{FFFC}";

/**
 * Drops the object-replacement character and every control character except
 * tab. Control characters survive a round trip through JSON and would then
 * render as invisible breakage the user cannot select or delete.
 */
export function sanitizeJournalRunText(text: string): string {
  let sanitized = "";
  for (const character of text) {
    if (character === JOURNAL_OBJECT_REPLACEMENT_CHARACTER) continue;
    const code = character.codePointAt(0) ?? 0;
    const isControl = (code <= 0x1f && character !== "\t") || code === 0x7f;
    if (isControl) continue;
    sanitized += character;
  }
  return sanitized;
}

/** Adjacent runs carrying identical marks coalesce so the encoding is minimal. */
export function coalesceJournalRuns(
  runs: readonly JournalTextRun[],
): readonly JournalTextRun[] {
  const merged: JournalTextRun[] = [];
  for (const run of runs) {
    if (run.text.length === 0) continue;
    const previous = merged[merged.length - 1];
    if (previous !== undefined && sameMarks(previous.marks, run.marks)) {
      merged[merged.length - 1] = {
        text: previous.text + run.text,
        marks: previous.marks,
      };
      continue;
    }
    merged.push(run);
  }
  return merged;
}

function sameMarks(
  left: readonly JournalTextMark[],
  right: readonly JournalTextMark[],
): boolean {
  if (left.length !== right.length) return false;
  return left.every((mark, index) => {
    const other = right[index];
    return (
      other !== undefined &&
      other.kind === mark.kind &&
      (other.value ?? null) === (mark.value ?? null)
    );
  });
}

// ---------------------------------------------------------------------------
// Effort link
// ---------------------------------------------------------------------------

/**
 * A frozen, deliberately small copy of the workout the entry is about. It is
 * text and numbers only — never a reference into effort history, and never raw
 * health series — so a recap stays intelligible after the underlying session is
 * deleted, and so deleting a session elsewhere cannot silently rewrite prose
 * the user already read back.
 */
export interface JournalEffortSnapshot {
  readonly activityName: string;
  readonly source: string;
  readonly startedAt: JournalInstant;
  /** Never earlier than startedAt. */
  readonly endedAt: JournalInstant;
  readonly activeDurationMilliseconds: Milliseconds;
  /** Never less than the active duration. */
  readonly wallDurationMilliseconds: Milliseconds;
  readonly outcome: string;
  /** Rendered as "{N} intervals" at capture time; absent for open efforts. */
  readonly intervalSummary?: string;
}

export interface JournalEffortLink {
  readonly sessionID: EffortSessionId;
  readonly snapshot: JournalEffortSnapshot;
}

// ---------------------------------------------------------------------------
// Prompts
// ---------------------------------------------------------------------------

export type JournalPromptFamily =
  | "workout"
  | "boxing"
  | "progress"
  | "recovery"
  | "fueling"
  | "consistency"
  | "planning";

export const JOURNAL_PROMPT_CATALOG_REVISION = 2;

/**
 * What is stored inside an entry: a snapshot, not a reference. A prompt whose
 * text changes in a later catalog revision must not retroactively change what
 * an old entry says it was answering.
 */
export interface JournalPromptReference {
  readonly id: JournalPromptId;
  readonly family?: JournalPromptFamily;
  readonly text: string;
  readonly catalogRevision?: number;
}

export interface JournalPromptCatalogEntry {
  readonly id: JournalPromptId;
  readonly family: JournalPromptFamily;
  readonly text: string;
}

export const JOURNAL_PROMPT_FAMILY_TITLES: Readonly<
  Record<JournalPromptFamily, string>
> = Object.freeze({
  workout: "Workout",
  boxing: "Boxing",
  progress: "Training progress",
  recovery: "Recovery",
  fueling: "Fueling",
  consistency: "Training habit",
  planning: "Next workout",
});

export const JOURNAL_PROMPT_FAMILY_ICONS: Readonly<
  Record<JournalPromptFamily, string>
> = Object.freeze({
  workout: "figure.run",
  boxing: "figure.boxing",
  progress: "chart.line.uptrend.xyaxis",
  recovery: "waveform.path.ecg",
  fueling: "fork.knife",
  consistency: "calendar.badge.checkmark",
  planning: "calendar.badge.plus",
});

/**
 * Prompt ids must be stable literals: an entry stores the id it answered, and
 * regenerating ids would orphan every stored prompt reference. The ids below
 * are pinned once for the web catalog and must never be regenerated, only
 * appended to.
 */
function promptId(sequence: number): JournalPromptId {
  return `00000000-0000-4000-8000-${String(sequence).padStart(12, "0")}` as JournalPromptId;
}

export const JOURNAL_PROMPT_CATALOG: readonly JournalPromptCatalogEntry[] =
  Object.freeze([
    {
      id: promptId(1),
      family: "workout",
      text: "Which exercise, round, or interval was strongest in today's workout?",
    },
    {
      id: promptId(2),
      family: "workout",
      text: "Where did your pace or power drop during this training session?",
    },
    {
      id: promptId(3),
      family: "workout",
      text: "What would you repeat or change in this workout next time?",
    },
    {
      id: promptId(4),
      family: "boxing",
      text: "Which punch, defense, or footwork cue improved your boxing form today?",
    },
    {
      id: promptId(5),
      family: "boxing",
      text: "Where did your guard, balance, or stance break down under fatigue?",
    },
    {
      id: promptId(6),
      family: "boxing",
      text: "Which boxing combination felt most natural, and what made it flow?",
    },
    {
      id: promptId(7),
      family: "progress",
      text: "What measurable training progress did you make this week?",
    },
    {
      id: promptId(8),
      family: "progress",
      text: "Which workout metric should improve next: load, reps, pace, rounds, or duration?",
    },
    {
      id: promptId(9),
      family: "progress",
      text: "What is the smallest next workout that advances your current fitness goal?",
    },
    {
      id: promptId(10),
      family: "recovery",
      text: "What soreness, pain, or movement restriction affected today's workout?",
    },
    {
      id: promptId(11),
      family: "recovery",
      text: "How did sleep and recovery affect your training readiness today?",
    },
    {
      id: promptId(12),
      family: "recovery",
      text: "What recovery action will help you train well next: rest, mobility, food, or sleep?",
    },
    {
      id: promptId(13),
      family: "fueling",
      text: "What did you eat before training, and how did it affect your energy?",
    },
    {
      id: promptId(14),
      family: "fueling",
      text: "Did you hydrate enough before, during, and after today's workout?",
    },
    {
      id: promptId(15),
      family: "fueling",
      text: "What post-workout meal or snack will support your recovery?",
    },
    {
      id: promptId(16),
      family: "consistency",
      text: "What made it easier or harder to complete today's workout?",
    },
    {
      id: promptId(17),
      family: "consistency",
      text: "What specific training habit helped you show up this week?",
    },
    {
      id: promptId(18),
      family: "consistency",
      text: "If your planned workout is disrupted, what shorter session will you do instead?",
    },
    {
      id: promptId(19),
      family: "planning",
      text: "What is the single training focus for your next workout?",
    },
    {
      id: promptId(20),
      family: "planning",
      text: "Which boxing skill or fitness capacity will your next session train?",
    },
    {
      id: promptId(21),
      family: "planning",
      text: "What workout will you schedule next, and when will you do it?",
    },
  ] as const);

export const JOURNAL_PROMPT_COUNT = JOURNAL_PROMPT_CATALOG.length;

/**
 * The daily prompt rotates on the calendar day and advances — never resets —
 * when the user asks for another. Resetting the offset would show the same
 * prompt again on the next visit and make the refresh button look broken.
 * Negative day ordinals (dates before the calendar era) must still land in
 * range, hence the double modulo.
 */
export function journalDailyPromptIndex(dayOrdinal: number, promptOffset: number): number {
  const raw = (dayOrdinal + promptOffset) % JOURNAL_PROMPT_COUNT;
  return ((raw % JOURNAL_PROMPT_COUNT) + JOURNAL_PROMPT_COUNT) % JOURNAL_PROMPT_COUNT;
}

export function journalPromptsInFamily(
  family: JournalPromptFamily,
): readonly JournalPromptCatalogEntry[] {
  return JOURNAL_PROMPT_CATALOG.filter((prompt) => prompt.family === family);
}

export function journalPromptReference(
  prompt: JournalPromptCatalogEntry,
): JournalPromptReference {
  return {
    id: prompt.id,
    family: prompt.family,
    text: prompt.text,
    catalogRevision: JOURNAL_PROMPT_CATALOG_REVISION,
  };
}

// ---------------------------------------------------------------------------
// Entry kinds, feeling, energy, reflection
// ---------------------------------------------------------------------------

export type JournalEntryKind =
  | "general"
  | "workoutRecap"
  | "goalReflection"
  | "personal";

/** Family → the kind a prompt-started entry adopts. */
export const JOURNAL_PROMPT_FAMILY_KIND: Readonly<
  Record<JournalPromptFamily, JournalEntryKind>
> = Object.freeze({
  workout: "workoutRecap",
  boxing: "workoutRecap",
  progress: "goalReflection",
  recovery: "general",
  fueling: "general",
  consistency: "general",
  planning: "general",
});

/** The Type menu and the composer's context summary. */
export const JOURNAL_ENTRY_KIND_TITLES: Readonly<Record<JournalEntryKind, string>> =
  Object.freeze({
    general: "Reflection",
    workoutRecap: "Training",
    goalReflection: "Goals",
    personal: "Personal",
  });

/**
 * The last link in a row's title fallback chain, which reads as a sentence
 * about the entry rather than as a menu label — hence the divergence from
 * JOURNAL_ENTRY_KIND_TITLES.
 */
export const JOURNAL_ENTRY_KIND_FALLBACK_TITLES: Readonly<
  Record<JournalEntryKind, string>
> = Object.freeze({
  general: "Reflection",
  workoutRecap: "Training recap",
  goalReflection: "Goal reflection",
  personal: "Personal",
});

export type JournalFeelingValence = -5 | -4 | -3 | -2 | -1 | 0 | 1 | 2 | 3 | 4 | 5;

export interface JournalFeeling {
  /** Trimmed and non-empty; a blank label is dropped rather than stored. */
  readonly label: string;
  readonly valence?: JournalFeelingValence;
}

export type JournalEnergyLevel = 1 | 2 | 3 | 4 | 5;

export const JOURNAL_ENERGY_LEVELS: readonly JournalEnergyLevel[] = Object.freeze([
  1, 2, 3, 4, 5,
] as const);

export type JournalPerceivedEffort = 1 | 2 | 3 | 4 | 5 | 6 | 7 | 8 | 9 | 10;

export const JOURNAL_PERCEIVED_EFFORT_RANGE = Object.freeze({ min: 1, max: 10 } as const);

/** The Readiness menu's fixed presets, in menu order. */
export const JOURNAL_READINESS_PRESETS: readonly {
  readonly label: string;
  readonly valence: JournalFeelingValence;
}[] = Object.freeze([
  { label: "Strong", valence: 3 },
  { label: "Steady", valence: 1 },
  { label: "Neutral", valence: 0 },
  { label: "Flat", valence: -1 },
  { label: "Frustrated", valence: -3 },
] as const);

export const JOURNAL_READINESS_NOT_ADDED = "Not added";

/**
 * Only meaningful alongside an effort link — a perceived-effort score with no
 * workout to score is uninterpretable — which is why the entry union below ties
 * the two together.
 */
export interface JournalWorkoutReflection {
  readonly perceivedEffort: JournalPerceivedEffort;
  /** Trimmed and non-empty; the whole reflection is dropped when it is blank. */
  readonly bodyObservation: string;
}

// ---------------------------------------------------------------------------
// Attachments
// ---------------------------------------------------------------------------

export type JournalMediaType = "photo" | "video" | "audio";

export type JournalAttachmentPurpose =
  | "general"
  | "progressPhoto"
  | "performanceVideo"
  | "formReference"
  | "voiceRecap";

export const JOURNAL_ATTACHMENT_BYTE_LIMITS: Readonly<
  Record<JournalMediaType, number>
> = Object.freeze({
  photo: 128 * 1024 * 1024,
  video: 1024 * 1024 * 1024,
  audio: 64 * 1024 * 1024,
});

/** Read from the image header without decoding pixels — a decompression bomb
 *  must be rejected before it is ever expanded into memory. */
export const JOURNAL_PHOTO_PIXEL_LIMIT = 100_000_000;
export const JOURNAL_PHOTO_DIMENSION_LIMIT = 32_768;
export const JOURNAL_VOICE_RECAP_MAX_DURATION: Milliseconds = 600_000;
/** Downsampling above this throws rather than allocating an unbounded bitmap. */
export const JOURNAL_MEDIA_MAX_THUMBNAIL_DIMENSION = 3_072;

interface JournalAttachmentShape {
  readonly schemaVersion: typeof JOURNAL_ATTACHMENT_SCHEMA_VERSION;
  readonly id: JournalAttachmentId;
  readonly ownerEntryID: JournalEntryId;
  readonly contentType: string;
  readonly byteCount: number;
  readonly contentSHA256: Sha256Hex;
  readonly caption?: string;
  readonly accessibilityDescription?: string;
  readonly createdAt: JournalInstant;
}

/**
 * Purpose determines media type, concealment, and whether a duration exists.
 * The union encodes all three: a progress photo is always a concealed photo,
 * an audio attachment is always a voice recap with a duration, and nothing else
 * carries a duration at all. An import request that asks for a visible progress
 * photo has no representable form here — which is the point, because the Swift
 * record forces concealment and a boolean field would let a caller believe it
 * had been honoured.
 */
export type JournalAttachment =
  | (JournalAttachmentShape & {
      readonly purpose: "progressPhoto";
      readonly mediaType: "photo";
      readonly isConcealed: true;
      readonly durationMilliseconds?: undefined;
    })
  | (JournalAttachmentShape & {
      readonly purpose: "performanceVideo";
      readonly mediaType: "video";
      readonly isConcealed: boolean;
      readonly durationMilliseconds?: undefined;
    })
  | (JournalAttachmentShape & {
      readonly purpose: "voiceRecap";
      readonly mediaType: "audio";
      readonly isConcealed: boolean;
      readonly durationMilliseconds: Milliseconds;
    })
  | (JournalAttachmentShape & {
      readonly purpose: "general" | "formReference";
      readonly mediaType: "photo" | "video";
      readonly isConcealed: boolean;
      readonly durationMilliseconds?: undefined;
    });

/**
 * The picker lets the user choose a purpose before the media type is known.
 * When the two disagree the purpose degrades to `general` rather than the
 * import failing — the user's file is the thing worth keeping.
 */
export function normalizeJournalAttachmentPurpose(
  purpose: JournalAttachmentPurpose,
  mediaType: JournalMediaType,
): JournalAttachmentPurpose {
  if (purpose === "progressPhoto" && mediaType !== "photo") return "general";
  if (purpose === "performanceVideo" && mediaType !== "video") return "general";
  return purpose;
}

export function journalAttachmentIsConcealed(
  purpose: JournalAttachmentPurpose,
  requested: boolean,
): boolean {
  // Concealment is a property of the purpose, not a preference.
  return purpose === "progressPhoto" ? true : requested;
}

/** Token strip and media-viewer titles. */
export function journalAttachmentTitle(attachment: JournalAttachment): string {
  switch (attachment.purpose) {
    case "progressPhoto":
      return JOURNAL_MEDIA_COPY.purposeProgressPhoto;
    case "performanceVideo":
      return JOURNAL_MEDIA_COPY.purposePerformanceVideo;
    case "formReference":
      return JOURNAL_MEDIA_COPY.purposeFormReference;
    case "voiceRecap": {
      const caption = attachment.caption?.trim();
      return caption !== undefined && caption.length > 0
        ? caption
        : JOURNAL_VOICE_COPY.title;
    }
    case "general":
      // Audio is unreachable here: an audio attachment is always a voice recap,
      // which the union above has already handled.
      return attachment.mediaType === "photo" ? "Photo" : "Video";
  }
}

export type JournalAttachmentIntegrity =
  | "verified"
  | "missing"
  | "byteCountMismatch"
  | "hashMismatch"
  | "unavailable";

/**
 * Attachments are sorted by id everywhere they appear, so the media embed order
 * inside the document is derivable from the attachment list rather than being a
 * second, divergent ordering.
 */
export function sortJournalAttachments(
  attachments: readonly JournalAttachment[],
): readonly JournalAttachment[] {
  return [...attachments].sort((left, right) =>
    left.id < right.id ? -1 : left.id > right.id ? 1 : 0,
  );
}

// ---------------------------------------------------------------------------
// Goals
// ---------------------------------------------------------------------------

export type JournalGoalStatus = "active" | "paused" | "completed" | "archived";

export const JOURNAL_GOAL_STATUS_TITLES: Readonly<Record<JournalGoalStatus, string>> =
  Object.freeze({
    active: "Active",
    paused: "Paused",
    completed: "Completed",
    archived: "Archived",
  });

export const JOURNAL_GOAL_STATUS_SORT_ORDER: Readonly<
  Record<JournalGoalStatus, number>
> = Object.freeze({ active: 0, paused: 1, completed: 2, archived: 3 });

export interface JournalGoal {
  readonly schemaVersion: typeof JOURNAL_GOAL_SCHEMA_VERSION;
  readonly id: JournalGoalId;
  readonly revision: JournalRevision;
  /** Trimmed; a goal with a blank title cannot be created. */
  readonly title: string;
  /** Trimmed; empty collapses to absent so "has a why" is one check. */
  readonly why?: string;
  readonly status: JournalGoalStatus;
  readonly targetDate?: JournalInstant;
  readonly createdAt: JournalInstant;
  readonly updatedAt: JournalInstant;
}

/**
 * An entry stores the goal's title as it read at the time, alongside the id.
 * The snapshot is intentionally retained after the goal is renamed or deleted:
 * a past reflection that silently re-titles itself, or loses its subject
 * entirely, is a rewrite of the user's own record.
 */
export interface JournalGoalLink {
  readonly goalID: JournalGoalId;
  readonly titleSnapshot: string;
}

export function journalGoalLink(goal: JournalGoal): JournalGoalLink {
  return { goalID: goal.id, titleSnapshot: goal.title };
}

/** Archived goals are terminal except for reactivation — nothing else applies. */
export function journalGoalLifecycleTransitions(
  status: JournalGoalStatus,
): readonly JournalGoalStatus[] {
  switch (status) {
    case "active":
      return ["paused", "completed", "archived"];
    case "paused":
      return ["active", "completed", "archived"];
    case "completed":
      return ["active", "archived"];
    case "archived":
      return ["active"];
  }
}

export type JournalGoalFilter = "current" | "completed" | "archived" | "every";

export const JOURNAL_GOAL_FILTER_TITLES: Readonly<Record<JournalGoalFilter, string>> =
  Object.freeze({
    current: "Current",
    completed: "Completed",
    archived: "Archived",
    every: "Every goal",
  });

export function journalGoalMatchesFilter(
  filter: JournalGoalFilter,
  goal: JournalGoal,
): boolean {
  switch (filter) {
    case "current":
      return goal.status === "active" || goal.status === "paused";
    case "completed":
      return goal.status === "completed";
    case "archived":
      return goal.status === "archived";
    case "every":
      return true;
  }
}

export function compareJournalGoals(left: JournalGoal, right: JournalGoal): number {
  const statusDelta =
    JOURNAL_GOAL_STATUS_SORT_ORDER[left.status] -
    JOURNAL_GOAL_STATUS_SORT_ORDER[right.status];
  if (statusDelta !== 0) return statusDelta;
  return right.updatedAt - left.updatedAt;
}

// ---------------------------------------------------------------------------
// Tags
// ---------------------------------------------------------------------------

/**
 * Case-, diacritic-, and width-insensitive folding. NFKD plus combining-mark
 * removal gives all three at once, and lowercasing last keeps the fold stable
 * for scripts where decomposition changes casing behaviour.
 */
export function foldJournalText(value: string): string {
  return value
    .normalize("NFKD")
    .replace(/\p{Diacritic}/gu, "")
    .toLowerCase();
}

/** Authoring is one comma-separated field; splitting is not reversible, so it
 *  happens once on save and never on decode. */
export function parseJournalTagField(raw: string): readonly string[] {
  return raw
    .split(",")
    .map((tag) => tag.trim())
    .filter((tag) => tag.length > 0);
}

/**
 * Deduplicates on the folded key while keeping the first original spelling, so
 * "Roadwork" typed before "roadwork" survives as the user wrote it. Sorting is
 * by folded key for determinism — a locale-aware sort would make the canonical
 * bytes depend on the device's language.
 */
export function normalizeJournalTags(tags: readonly string[]): readonly string[] {
  const byKey = new Map<string, string>();
  for (const tag of tags) {
    const trimmed = tag.trim();
    if (trimmed.length === 0) continue;
    const key = foldJournalText(trimmed);
    if (!byKey.has(key)) byKey.set(key, trimmed);
  }
  return [...byKey.entries()]
    .sort(([left], [right]) => (left < right ? -1 : left > right ? 1 : 0))
    .map(([, original]) => original);
}

export const JOURNAL_TAG_SEPARATOR = " · ";

// ---------------------------------------------------------------------------
// Migration marker
// ---------------------------------------------------------------------------

export type JournalMigrationSource = "legacyCornerNote";

/**
 * Proof that a legacy record already became this entry. It is what makes the
 * migration idempotent: without the marker a second pass would create a second
 * recap for the same session, and the legacy source is deliberately retained
 * rather than deleted after the merge.
 */
export interface JournalMigrationMarker {
  readonly source: JournalMigrationSource;
  /** Always equal to the entry's own id. */
  readonly sourceID: JournalEntryId;
  readonly sourceSchemaVersion: number;
  readonly sourceContentSHA256: Sha256Hex;
}

export type JournalMigrationIssue = "missingEffortSnapshot" | "conflictingEntry";

export type JournalMigrationOutcome =
  | { readonly kind: "nothingToMigrate" }
  | { readonly kind: "alreadyMigrated" }
  | { readonly kind: "quarantine"; readonly issue: JournalMigrationIssue }
  | { readonly kind: "save"; readonly candidate: JournalEntry };

// ---------------------------------------------------------------------------
// Entry
// ---------------------------------------------------------------------------

interface JournalEntryShape {
  readonly schemaVersion: typeof JOURNAL_ENTRY_SCHEMA_VERSION;
  readonly id: JournalEntryId;
  readonly revision: JournalRevision;
  /**
   * The moment written *about*, which the user can move freely. Deliberately
   * separate from createdAt/updatedAt so back-dating yesterday's session does
   * not falsify when the writing happened.
   */
  readonly occurredAt: JournalInstant;
  readonly createdAt: JournalInstant;
  /** Never earlier than createdAt. */
  readonly updatedAt: JournalInstant;
  readonly title?: string;
  readonly document: JournalDocument;
  /** Absent is legal and reads as `general`; see journalEntryKind. */
  readonly kind?: JournalEntryKind;
  readonly feeling?: JournalFeeling;
  readonly energy?: JournalEnergyLevel;
  readonly isFavorite: boolean;
  /** Folded-deduplicated and sorted on save; preserved byte-exact on decode. */
  readonly tags: readonly string[];
  readonly prompt?: JournalPromptReference;
  readonly goal?: JournalGoalLink;
  /** Sorted by id; the media embed set must equal this set exactly. */
  readonly attachments: readonly JournalAttachment[];
  readonly migration?: JournalMigrationMarker;
}

/**
 * A workout reflection cannot exist without the effort it reflects on, so the
 * two travel in the same union arm. Splitting here also documents the
 * immutability rule: `effort` is frozen across revisions, and an entry that
 * never had one can never acquire one.
 */
export type JournalEntry =
  | (JournalEntryShape & {
      readonly effort: JournalEffortLink;
      readonly workoutReflection?: JournalWorkoutReflection;
    })
  | (JournalEntryShape & {
      readonly effort?: undefined;
      readonly workoutReflection?: undefined;
    });

/** Fields frozen across revisions; changing one is `immutableContextChanged`. */
export const JOURNAL_ENTRY_IMMUTABLE_FIELDS: readonly (keyof JournalEntryShape | "effort")[] =
  Object.freeze(["createdAt", "effort", "migration"] as const);

export function journalEntryKind(entry: JournalEntry): JournalEntryKind {
  // A missing kind is not "unknown" — legacy entries predate the field.
  return entry.kind ?? "general";
}

// ---------------------------------------------------------------------------
// Draft and the draft-overlay projection
// ---------------------------------------------------------------------------

export interface JournalDraft {
  readonly schemaVersion: typeof JOURNAL_DRAFT_SCHEMA_VERSION;
  /** Always equal to `entry.id`. */
  readonly id: JournalDraftId;
  readonly entry: JournalEntry;
  /** Strictly increasing; drafts order by timestamp, not by revision. */
  readonly savedAt: JournalInstant;
}

/**
 * The only value Journal presents for a durable entry. Every reader, the
 * timeline, search, favourites, and export all consume projections, because
 * showing the durable revision while a newer same-id autosave exists would show
 * the user text they already replaced.
 */
export type JournalEntryProjection =
  | {
      readonly isDraft: true;
      /** The draft's entry — the working truth. */
      readonly entry: JournalEntry;
      readonly draft: JournalDraft;
    }
  | {
      readonly isDraft: false;
      readonly entry: JournalEntry;
      readonly draft?: undefined;
    };

export function journalProjection(
  entry: JournalEntry,
  draft: JournalDraft | undefined,
): JournalEntryProjection {
  if (draft === undefined) return { isDraft: false, entry };
  return { isDraft: true, entry: draft.entry, draft };
}

/**
 * Timeline order. Working (draft-overlaid) dates are used deliberately, so
 * editing an entry's `occurredAt` in a draft reorders it immediately instead of
 * jumping once the draft promotes.
 */
export function compareJournalProjections(
  left: JournalEntryProjection,
  right: JournalEntryProjection,
): number {
  const occurredDelta = right.entry.occurredAt - left.entry.occurredAt;
  if (occurredDelta !== 0) return occurredDelta;
  const updatedDelta = right.entry.updatedAt - left.entry.updatedAt;
  if (updatedDelta !== 0) return updatedDelta;
  return left.entry.id < right.entry.id ? -1 : left.entry.id > right.entry.id ? 1 : 0;
}

/**
 * Drafts with no persisted counterpart. They are excluded from the timeline —
 * an unfinished thought is not yet a journal entry — and reachable only through
 * Continue writing.
 */
export function journalStandaloneDrafts(
  drafts: readonly JournalDraft[],
  persistedEntryIds: ReadonlySet<JournalEntryId>,
): readonly JournalDraft[] {
  return drafts
    .filter((draft) => !persistedEntryIds.has(draft.id))
    .sort((left, right) => {
      const savedDelta = right.savedAt - left.savedAt;
      if (savedDelta !== 0) return savedDelta;
      return left.id < right.id ? -1 : left.id > right.id ? 1 : 0;
    });
}

// ---------------------------------------------------------------------------
// Title and preview fallback chains
// ---------------------------------------------------------------------------

function firstNonEmpty(...candidates: (string | undefined)[]): string | undefined {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed !== undefined && trimmed.length > 0) return trimmed;
  }
  return undefined;
}

/**
 * An untitled entry still needs a stable name in the timeline. The chain runs
 * from most specific to least so a recap reads as its workout rather than as
 * the generic kind label.
 */
export function journalEntryTitle(entry: JournalEntry): string {
  return (
    firstNonEmpty(
      entry.title,
      entry.prompt?.text,
      entry.effort?.snapshot.activityName,
      entry.goal?.titleSnapshot,
    ) ?? JOURNAL_ENTRY_KIND_FALLBACK_TITLES[journalEntryKind(entry)]
  );
}

/**
 * `liveGoalWhy` is passed in rather than looked up: the domain cannot reach the
 * goal library, and the whole point of the goal *link* is that the live goal
 * may no longer exist.
 */
export function journalEntryPreview(
  entry: JournalEntry,
  liveGoalWhy?: string,
): string {
  const documentText = entry.document.blocks
    .filter(isJournalTextBlock)
    .map((block) => journalRunPlainText(block.runs).trim())
    .filter((text) => text.length > 0)
    .join(" ");
  return (
    firstNonEmpty(
      documentText,
      entry.workoutReflection?.bodyObservation,
      liveGoalWhy,
    ) ?? JOURNAL_TIMELINE_COPY.noWrittenReflection
  );
}

// ---------------------------------------------------------------------------
// Lens, date filter, search
// ---------------------------------------------------------------------------

export type JournalLens = "all" | "training" | "goals" | "personal" | "favorites";

export const JOURNAL_LENSES: readonly JournalLens[] = Object.freeze([
  "all",
  "training",
  "goals",
  "personal",
  "favorites",
] as const);

export const JOURNAL_LENS_TITLES: Readonly<Record<JournalLens, string>> =
  Object.freeze({
    all: "All",
    training: "Training",
    goals: "Goals",
    personal: "Personal",
    favorites: "Favorites",
  });

/**
 * Membership is deliberately generous on both ends: an entry with an effort
 * link is Training even if it was never typed as a recap, and an entry with no
 * kind at all is Personal rather than falling out of every lens.
 */
export function journalLensMatches(lens: JournalLens, entry: JournalEntry): boolean {
  switch (lens) {
    case "all":
      return true;
    case "training":
      return entry.kind === "workoutRecap" || entry.effort !== undefined;
    case "goals":
      return entry.kind === "goalReflection" || entry.goal !== undefined;
    case "personal":
      return (
        entry.kind === "personal" ||
        entry.kind === "general" ||
        entry.kind === undefined
      );
    case "favorites":
      return entry.isFavorite;
  }
}

/**
 * Field order is part of the contract: the index is one newline-joined string
 * and results preserve index order, so reordering the fields changes which
 * entries a partial query matches first.
 */
export function journalSearchIndex(projection: JournalEntryProjection): string {
  const entry = projection.entry;
  const fields: (string | undefined)[] = [
    entry.title,
    journalDocumentPlainText(entry.document),
    entry.feeling?.label,
    entry.feeling?.valence?.toString(),
    entry.energy?.toString(),
    entry.kind,
    entry.prompt?.text,
    entry.prompt?.family,
    entry.effort?.snapshot.activityName,
    entry.effort?.snapshot.source,
    entry.effort?.snapshot.outcome,
    entry.effort?.snapshot.intervalSummary,
    entry.workoutReflection?.perceivedEffort.toString(),
    entry.workoutReflection?.bodyObservation,
    entry.goal?.titleSnapshot,
    ...entry.attachments.map((attachment) => attachment.caption),
    ...entry.attachments.map((attachment) => attachment.accessibilityDescription),
    ...entry.attachments.map((attachment) => attachment.contentType),
    ...entry.attachments.map((attachment) => attachment.mediaType),
    ...entry.attachments.map((attachment) => attachment.purpose),
    ...entry.tags,
  ];
  return fields
    .filter((field): field is string => field !== undefined && field.length > 0)
    .join("\n");
}

export function journalSearchMatches(query: string, index: string): boolean {
  const trimmed = query.trim();
  // An empty query is not "no results" — it is the unfiltered timeline.
  if (trimmed.length === 0) return true;
  return foldJournalText(index).includes(foldJournalText(trimmed));
}

// ---------------------------------------------------------------------------
// Canonical document assembly
// ---------------------------------------------------------------------------

export interface JournalDocumentAssembly {
  readonly effort?: {
    readonly blockId: JournalBlockId;
    readonly link: JournalEffortLink;
  };
  readonly prompt?: {
    readonly blockId: JournalBlockId;
    readonly prompt: JournalPromptReference;
  };
  readonly textBlocks: readonly JournalTextBlock[];
  readonly media: readonly {
    readonly blockId: JournalBlockId;
    readonly attachmentId: JournalAttachmentId;
  }[];
}

/**
 * Fixed save order: effort, prompt, the editor's text, then one media embed per
 * attachment. Embed block ids are carried in rather than minted here so a save
 * that changes nothing re-encodes to identical bytes — the write verifier
 * compares canonical encodings, and a fresh UUID would make every save look
 * like a real change.
 */
export function assembleJournalDocument(
  assembly: JournalDocumentAssembly,
): JournalDocument {
  const blocks: JournalBlock[] = [];
  if (assembly.effort !== undefined) {
    blocks.push({
      kind: "embed",
      id: assembly.effort.blockId,
      embed: { kind: "effort", effort: assembly.effort.link },
    });
  }
  if (assembly.prompt !== undefined) {
    blocks.push({
      kind: "embed",
      id: assembly.prompt.blockId,
      embed: { kind: "prompt", prompt: assembly.prompt.prompt },
    });
  }
  blocks.push(...assembly.textBlocks);
  const media = [...assembly.media].sort((left, right) =>
    left.attachmentId < right.attachmentId
      ? -1
      : left.attachmentId > right.attachmentId
        ? 1
        : 0,
  );
  for (const item of media) {
    blocks.push({
      kind: "embed",
      id: item.blockId,
      embed: { kind: "media", media: { attachmentId: item.attachmentId } },
    });
  }
  return { schemaVersion: JOURNAL_DOCUMENT_SCHEMA_VERSION, blocks };
}

// ---------------------------------------------------------------------------
// Composer seeds, recap identity, autosave, finish
// ---------------------------------------------------------------------------

export interface JournalRecapRequest {
  readonly sessionID: EffortSessionId;
  readonly snapshot: JournalEffortSnapshot;
  /** Trimmed; empty collapses to absent. */
  readonly initialNote?: string;
  readonly perceivedEffort?: JournalPerceivedEffort;
}

/**
 * Every writing route funnels through one seed so there is exactly one composer
 * and one set of gating rules. `workingRevision` is reserved up front — a draft
 * keeps the revision it already claimed, so promotion still records exactly one
 * increment no matter how many autosaves happened in between.
 */
export type JournalComposerSeed =
  | {
      readonly kind: "blank";
      readonly entryId: JournalEntryId;
      readonly entryKind: JournalEntryKind;
      readonly prompt?: JournalPromptReference;
      readonly goal?: JournalGoalLink;
      readonly occurredAt: JournalInstant;
      readonly workingRevision: JournalRevision;
    }
  | {
      readonly kind: "editing";
      readonly entry: JournalEntry;
      readonly workingRevision: JournalRevision;
    }
  | {
      readonly kind: "resuming";
      readonly draft: JournalDraft;
      readonly workingRevision: JournalRevision;
    }
  | {
      readonly kind: "workoutRecap";
      readonly request: JournalRecapRequest;
      /** The session UUID *is* the entry id, which is what makes recaps stable. */
      readonly entryId: JournalEntryId;
      readonly workingRevision: JournalRevision;
    }
  | {
      readonly kind: "merging";
      readonly request: JournalRecapRequest;
      readonly existing: JournalEntry;
      readonly workingRevision: JournalRevision;
    };

/**
 * Recap routing. Matching is on `effort.sessionID` rather than on the entry id
 * so legacy recaps written under a non-canonical UUID resume instead of
 * silently duplicating. A record that owns the session's *identity* but carries
 * a different effort link is a genuine collision and is refused rather than
 * merged — merging would overwrite one workout's notes with another's.
 */
export type JournalRecapResolution =
  | { readonly kind: "refusedByDraft" }
  | { readonly kind: "resumeDraft"; readonly draft: JournalDraft }
  | { readonly kind: "refusedByEntry" }
  | { readonly kind: "editEntry"; readonly entry: JournalEntry }
  | { readonly kind: "newRecap" };

export type JournalAutosaveState =
  | { readonly kind: "untouched" }
  | { readonly kind: "waiting" }
  | { readonly kind: "saving" }
  | { readonly kind: "saved"; readonly at: JournalInstant }
  | { readonly kind: "failed"; readonly message: string };

export const JOURNAL_AUTOSAVE_DEBOUNCE: Milliseconds = 650;

export function journalAutosaveLabel(state: JournalAutosaveState): string {
  switch (state.kind) {
    case "untouched":
      return JOURNAL_AUTOSAVE_COPY.untouched;
    case "waiting":
      return JOURNAL_AUTOSAVE_COPY.waiting;
    case "saving":
      return JOURNAL_AUTOSAVE_COPY.saving;
    case "saved":
      return JOURNAL_AUTOSAVE_COPY.saved;
    case "failed":
      return JOURNAL_AUTOSAVE_COPY.failed;
  }
}

/** `saved` and `untouched` are the only states with nothing outstanding. */
export function journalHasUnsavedChanges(state: JournalAutosaveState): boolean {
  return state.kind === "waiting" || state.kind === "saving" || state.kind === "failed";
}

export type JournalFinishInterruption =
  | "alreadyFinishing"
  | "mediaImportInProgress"
  | "concurrentMutation"
  | "persistenceFailed"
  | "cleanupFailed";

export type JournalFinishOutcome =
  | { readonly kind: "saved"; readonly entry: JournalEntry }
  | { readonly kind: "discardedBlank" }
  | { readonly kind: "keptOpen"; readonly interruption: JournalFinishInterruption };

/**
 * Only a completed transaction closes the composer. Anything else leaves the
 * user's text on screen — dismissing on a failed promotion is how writing gets
 * lost with no way back.
 */
export function journalFinishDismissesComposer(outcome: JournalFinishOutcome): boolean {
  return outcome.kind === "saved" || outcome.kind === "discardedBlank";
}

export type JournalComposerOrigin = "new" | "persisted" | "draft";

/**
 * Whether the close button raises the discard dialog. A new-but-blank entry has
 * nothing to discard, and a persisted entry with no unsaved change and no
 * shadowing draft is already exactly as the user left it.
 */
export function journalOffersDiscard(input: {
  readonly origin: JournalComposerOrigin;
  readonly isBlank: boolean;
  readonly hasUnsavedChanges: boolean;
  readonly hasSameIdDraft: boolean;
}): boolean {
  switch (input.origin) {
    case "draft":
      return true;
    case "new":
      return !input.isBlank;
    case "persisted":
      return input.hasUnsavedChanges || input.hasSameIdDraft;
  }
}

// ---------------------------------------------------------------------------
// Voice recap
// ---------------------------------------------------------------------------

/**
 * A finished, verified clip. Duration comes from decoding the file, never from
 * the recorder's reported elapsed time: a recorder that stalls still ticks, and
 * the stored duration must describe the bytes that actually exist.
 */
export interface JournalVoiceRecording {
  readonly stagedMediaId: JournalStagedMediaId;
  readonly durationMilliseconds: Milliseconds;
  readonly byteCount: number;
  readonly contentType: string;
}

export type JournalVoiceRecorderPhase =
  | { readonly kind: "idle" }
  | { readonly kind: "requestingPermission" }
  | { readonly kind: "recording" }
  | { readonly kind: "paused" }
  | { readonly kind: "reviewing"; readonly recording: JournalVoiceRecording }
  | { readonly kind: "permissionDenied" }
  | { readonly kind: "failed"; readonly message: string };

export interface JournalVoiceRecorderState {
  readonly phase: JournalVoiceRecorderPhase;
  readonly elapsed: Milliseconds;
  readonly playbackPosition: Milliseconds;
  /** Rolling window; the visualiser is fixed-width so the window is too. */
  readonly meterLevels: readonly number[];
  readonly isPlaying: boolean;
  readonly notice?: string;
}

export const JOURNAL_VOICE_METER_WINDOW = 30;
export const JOURNAL_VOICE_METER_INITIAL_LEVEL = 0.05;
export const JOURNAL_VOICE_METER_FLOOR = 0.04;
export const JOURNAL_VOICE_CLOCK_INTERVAL: Milliseconds = 100;
export const JOURNAL_VOICE_PLAYBACK_POLL_INTERVAL: Milliseconds = 150;
/** Restart-from-zero and auto-stop both use this end-of-item tolerance. */
export const JOURNAL_VOICE_END_TOLERANCE: Milliseconds = 100;

export const JOURNAL_VOICE_ENCODER = Object.freeze({
  format: "aac",
  sampleRate: 48_000,
  channels: 1,
  bitRate: 64_000,
  fileExtension: "m4a",
} as const);

/**
 * Log-scaled so quiet speech still moves the waveform, floored so a silent
 * microphone still draws a visible baseline rather than looking broken.
 */
export function normalizeJournalMeterLevel(decibels: number): number {
  return Math.max(
    JOURNAL_VOICE_METER_FLOOR,
    Math.min(1, Math.pow(10, decibels / 28)),
  );
}

/**
 * Retrying a failed start must not be possible while a staged clip is still
 * owned — starting again would strand the first recording's bytes with no
 * owner to clean them up.
 */
export function journalVoiceCanStart(
  phase: JournalVoiceRecorderPhase,
  ownsStagedClip: boolean,
): boolean {
  switch (phase.kind) {
    case "idle":
    case "permissionDenied":
      return true;
    case "failed":
      return !ownsStagedClip;
    default:
      return false;
  }
}

export function journalVoiceCanAttach(phase: JournalVoiceRecorderPhase): boolean {
  return phase.kind === "reviewing";
}

/** Always MM:SS — a ten-minute cap means an hours field would only ever be 00. */
export function journalAudioClock(duration: Milliseconds): string {
  const totalSeconds = Math.floor(Math.max(0, duration) / 1_000);
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${String(minutes).padStart(2, "0")}:${String(seconds).padStart(2, "0")}`;
}

/**
 * The web build unifies on @ff/domain's `spokenDuration`, which omits a zero
 * component. The Swift app had two spoken formatters that disagreed about
 * whether to say "zero seconds"; assistive technology reading two different
 * phrasings for the same duration is a defect, not a nuance worth porting.
 */
export function journalSpokenPosition(
  position: Milliseconds,
  total: Milliseconds,
): string {
  return `${spokenDuration(position)} of ${spokenDuration(total)}`;
}

export type JournalVoiceInterruption =
  | "audioInterruption"
  | "routeChange"
  | "mediaServicesReset"
  | "background";

export const JOURNAL_VOICE_INTERRUPTION_NOTICES: Readonly<
  Record<JournalVoiceInterruption, string>
> = Object.freeze({
  audioInterruption:
    "Recording paused by an audio interruption. Resume when you're ready.",
  routeChange:
    "Recording paused because the audio route changed. Resume when you're ready.",
  mediaServicesReset:
    "Audio services restarted. Review or discard this recording before trying again.",
  background:
    "Recording paused when Fighting Form left the foreground. Resume when you're ready.",
});

// ---------------------------------------------------------------------------
// Privacy: authentication availability
// ---------------------------------------------------------------------------

export type JournalAuthenticationAvailability =
  | "faceID"
  | "touchID"
  | "opticID"
  | "passcode"
  | "unavailable";

/**
 * These labels are user-facing text, not diagnostics: they appear as the lock
 * toggle's trailing label and inside sentences ("Use face id before opening
 * Journal"), which is why the lowercased form is derived rather than stored.
 */
export const JOURNAL_AVAILABILITY_LABELS: Readonly<
  Record<JournalAuthenticationAvailability, string>
> = Object.freeze({
  faceID: "Face ID",
  touchID: "Touch ID",
  opticID: "Optic ID",
  passcode: "Device passcode",
  unavailable: "Unavailable",
});

/** The desktop build has no device passcode concept to point at. */
export const JOURNAL_AVAILABILITY_LABELS_DESKTOP: Readonly<
  Record<JournalAuthenticationAvailability, string>
> = Object.freeze({
  ...JOURNAL_AVAILABILITY_LABELS,
  passcode: "Device owner",
});

export function journalAuthenticationAvailable(
  availability: JournalAuthenticationAvailability,
): boolean {
  return availability !== "unavailable";
}

export const JOURNAL_AUTH_REASONS = Object.freeze({
  openJournal: "Open your Fighting Form journal",
  turnLockOn: "Turn on Journal Lock",
  turnLockOff: "Turn off Journal Lock",
  turnLockOnLong: "Turn on Fighting Form Journal Lock",
  turnLockOffLong: "Turn off Fighting Form Journal Lock",
  writeRecap: "Write a private Fighting Form workout recap",
  readReflection: "Read your private Fighting Form reflection",
  openEntry: "Open your Fighting Form journal entry",
} as const);

/** The prompt's cancel button is never "Cancel" — declining is not an error. */
export const JOURNAL_AUTH_CANCEL_TITLE = "Not now";

// ---------------------------------------------------------------------------
// Privacy: the foreground lease
// ---------------------------------------------------------------------------

/**
 * Proof that *this uninterrupted foreground session* verified the device owner.
 * It exists for exactly one purpose: letting a user who can still prove they
 * are the owner turn Journal Lock **off** after device authentication breaks.
 * It can never turn a lock on, and it does not survive the app leaving the
 * foreground.
 */
export interface JournalForegroundLease {
  readonly grantedAt: JournalInstant;
  readonly verifiedBy: Exclude<JournalAuthenticationAvailability, "unavailable">;
}

/**
 * How a revealed vault came to be revealed. `lockDisabled` deliberately carries
 * no lease: unlocking with the lock off verifies nothing, and treating it as
 * verification would let a user with no passcode turn the lock off on someone
 * else's device.
 */
export type JournalRevealVerification =
  | { readonly kind: "lockDisabled" }
  | {
      readonly kind: "verifiedForegroundLease";
      readonly lease: JournalForegroundLease;
    };

// ---------------------------------------------------------------------------
// Privacy: the vault state machine
// ---------------------------------------------------------------------------

/**
 * Everything a revealed Journal can read. It exists only inside the `revealed`
 * variant of JournalVault.
 */
export interface JournalOpenLibrary {
  readonly projections: readonly JournalEntryProjection[];
  readonly standaloneDrafts: readonly JournalDraft[];
  readonly goals: readonly JournalGoal[];
  readonly recoveries: readonly JournalRecoveryItem[];
  /** Set when some data needs recovery; absent on a clean load. */
  readonly notice?: string;
}

export type JournalConcealedResume =
  /** Lock on: a fresh device-owner check is required. */
  | { readonly kind: "requiresAuthentication" }
  /** Lock off: concealment is still applied, but a plain Continue lifts it. */
  | { readonly kind: "requiresAcknowledgement" };

/**
 * The Journal Lock state machine.
 *
 * Only `revealed` has a `library` field. Concealment is therefore not a flag
 * that a view could ignore, a serializer could skip, or a devtools inspector
 * could see past — while the vault is not revealed there is no field anywhere
 * in the value that holds entry text. Re-revealing requires the caller to
 * supply a freshly loaded library, which is why the reveal events carry one.
 */
export type JournalVault =
  | { readonly state: "preparing" }
  | {
      readonly state: "unavailable";
      readonly message: string;
      /** Preserved bytes are safe to surface: they are metadata, not prose. */
      readonly recoveries: readonly JournalRecoveryItem[];
    }
  | {
      readonly state: "concealed";
      readonly resume: JournalConcealedResume;
      readonly availability: JournalAuthenticationAvailability;
    }
  | {
      readonly state: "authenticating";
      readonly availability: JournalAuthenticationAvailability;
    }
  | {
      readonly state: "revealed";
      readonly verification: JournalRevealVerification;
      readonly availability: JournalAuthenticationAvailability;
      readonly library: JournalOpenLibrary;
    };

export const JOURNAL_VAULT_INITIAL: JournalVault = Object.freeze({
  state: "preparing",
});

export type JournalVaultEvent =
  | {
      readonly type: "prepared";
      readonly library: JournalOpenLibrary;
      readonly lockEnabled: boolean;
      readonly availability: JournalAuthenticationAvailability;
    }
  | {
      readonly type: "preparationFailed";
      readonly message: string;
      readonly recoveries: readonly JournalRecoveryItem[];
    }
  | { readonly type: "revealRequested" }
  | {
      readonly type: "revealSucceeded";
      readonly at: JournalInstant;
      readonly verifiedBy: Exclude<JournalAuthenticationAvailability, "unavailable">;
      readonly library: JournalOpenLibrary;
    }
  | { readonly type: "revealAcknowledged"; readonly library: JournalOpenLibrary }
  | {
      readonly type: "revealFailed";
      readonly availability: JournalAuthenticationAvailability;
    }
  | { readonly type: "revealCancelled" }
  | { readonly type: "libraryRefreshed"; readonly library: JournalOpenLibrary }
  /** Foreground loss. Revokes the lease and drops the library, lock or no lock. */
  | { readonly type: "foregroundLost"; readonly lockEnabled: boolean }
  | { readonly type: "lockEnabledChanged"; readonly lockEnabled: boolean }
  | {
      readonly type: "availabilityChanged";
      readonly availability: JournalAuthenticationAvailability;
    };

function vaultAvailability(vault: JournalVault): JournalAuthenticationAvailability {
  switch (vault.state) {
    case "concealed":
    case "authenticating":
    case "revealed":
      return vault.availability;
    default:
      return "unavailable";
  }
}

/**
 * The whole lock machine in one pure reduction, so its fail-closed properties
 * are testable without a device, an authentication framework, or a browser.
 *
 * Two rules are load-bearing and repeated in several arms:
 *  - Any path out of `revealed` drops `library` entirely.
 *  - No path reaches `revealed` without a caller-supplied library, so content
 *    cannot be "restored" from a stale reference held by the state itself.
 */
export function reduceJournalVault(
  vault: JournalVault,
  event: JournalVaultEvent,
): JournalVault {
  switch (event.type) {
    case "prepared":
      if (event.lockEnabled) {
        return {
          state: "concealed",
          resume: { kind: "requiresAuthentication" },
          availability: event.availability,
        };
      }
      return {
        state: "revealed",
        verification: { kind: "lockDisabled" },
        availability: event.availability,
        library: event.library,
      };

    case "preparationFailed":
      return {
        state: "unavailable",
        message: event.message,
        recoveries: event.recoveries,
      };

    case "revealRequested": {
      if (vault.state !== "concealed") return vault;
      // Fail closed: with authentication unavailable there is nothing to ask,
      // and prompting would either no-op or, worse, appear to succeed.
      if (vault.resume.kind === "requiresAcknowledgement") return vault;
      if (!journalAuthenticationAvailable(vault.availability)) return vault;
      return { state: "authenticating", availability: vault.availability };
    }

    case "revealSucceeded": {
      if (vault.state !== "authenticating" && vault.state !== "concealed") return vault;
      return {
        state: "revealed",
        verification: {
          kind: "verifiedForegroundLease",
          lease: { grantedAt: event.at, verifiedBy: event.verifiedBy },
        },
        availability: event.verifiedBy,
        library: event.library,
      };
    }

    case "revealAcknowledged": {
      if (vault.state !== "concealed") return vault;
      // Acknowledgement lifts concealment only where nothing was ever locked.
      if (vault.resume.kind !== "requiresAcknowledgement") return vault;
      return {
        state: "revealed",
        verification: { kind: "lockDisabled" },
        availability: vault.availability,
        library: event.library,
      };
    }

    case "revealFailed":
      return {
        state: "concealed",
        resume: { kind: "requiresAuthentication" },
        availability: event.availability,
      };

    case "revealCancelled": {
      if (vault.state !== "authenticating") return vault;
      return {
        state: "concealed",
        resume: { kind: "requiresAuthentication" },
        availability: vault.availability,
      };
    }

    case "libraryRefreshed": {
      // A refresh can only replace content that is already legitimately open.
      if (vault.state !== "revealed") return vault;
      return { ...vault, library: event.library };
    }

    case "foregroundLost": {
      if (vault.state === "preparing" || vault.state === "unavailable") return vault;
      return {
        state: "concealed",
        resume: event.lockEnabled
          ? { kind: "requiresAuthentication" }
          : { kind: "requiresAcknowledgement" },
        availability: vaultAvailability(vault),
      };
    }

    case "lockEnabledChanged": {
      if (vault.state === "preparing" || vault.state === "unavailable") return vault;
      if (event.lockEnabled) {
        // Turning the lock on conceals immediately, even mid-composition — the
        // user just asked for the content to stop being visible.
        return {
          state: "concealed",
          resume: { kind: "requiresAuthentication" },
          availability: vaultAvailability(vault),
        };
      }
      if (vault.state === "concealed") {
        // Turning the lock off unlocks, but the library still has to be handed
        // back in through revealAcknowledged; the concealed state never held it.
        return {
          state: "concealed",
          resume: { kind: "requiresAcknowledgement" },
          availability: vault.availability,
        };
      }
      return vault;
    }

    case "availabilityChanged": {
      switch (vault.state) {
        case "concealed":
        case "authenticating":
        case "revealed":
          return { ...vault, availability: event.availability };
        default:
          return vault;
      }
    }
  }
}

/**
 * The only accessor for entry content. Callers cannot destructure a library out
 * of a concealed vault, and this function makes the single legal path explicit
 * enough that a code review can grep for it.
 */
export function journalOpenLibrary(vault: JournalVault): JournalOpenLibrary | null {
  return vault.state === "revealed" ? vault.library : null;
}

export function journalForegroundLease(
  vault: JournalVault,
): JournalForegroundLease | null {
  if (vault.state !== "revealed") return null;
  return vault.verification.kind === "verifiedForegroundLease"
    ? vault.verification.lease
    : null;
}

/**
 * Whether the window-level shield must be up. Sensitivity is a property of what
 * is on screen, not of the lock: an open composer is sensitive even with the
 * lock off, because the OS snapshots the app switcher regardless.
 */
export function journalIsPrivacySensitive(input: {
  readonly isJournalNavigationActive: boolean;
  readonly isComposerPresented: boolean;
}): boolean {
  return input.isJournalNavigationActive || input.isComposerPresented;
}

// ---------------------------------------------------------------------------
// Privacy: lock-change authorization
// ---------------------------------------------------------------------------

export const JOURNAL_LOCK_SETTING_KEY = "journal.lockEnabled";
/** The app never flips this by itself, including when authentication breaks. */
export const JOURNAL_LOCK_DEFAULT = false;

export type JournalLockChangePlan =
  | { readonly kind: "noChange" }
  | { readonly kind: "requiresAuthentication"; readonly reason: string }
  /** The one escape hatch, and only ever in the off direction. */
  | { readonly kind: "authorizedByForegroundLease" }
  | { readonly kind: "authenticationUnavailable" };

export type JournalLockChangeOutcome =
  | { readonly kind: "noChange" }
  | { readonly kind: "authorizedByAuthentication" }
  | { readonly kind: "authorizedByForegroundLease" }
  | { readonly kind: "authenticationUnavailable" }
  | { readonly kind: "authenticationFailed" };

/**
 * Never fails open. With authentication unavailable the *only* permitted change
 * is turning an existing lock off from an already-verified foreground session —
 * anything else leaves the persisted setting untouched, including the case
 * where the user is locked out. Being locked out of your own journal is
 * recoverable; a journal that quietly unlocks itself is not.
 */
export function planJournalLockChange(input: {
  readonly current: boolean;
  readonly requested: boolean;
  readonly availability: JournalAuthenticationAvailability;
  readonly isUnlocked: boolean;
  readonly hasVerifiedForegroundLease: boolean;
}): JournalLockChangePlan {
  if (input.current === input.requested) return { kind: "noChange" };

  if (!journalAuthenticationAvailable(input.availability)) {
    const escapeHatch =
      input.current &&
      !input.requested &&
      input.isUnlocked &&
      input.hasVerifiedForegroundLease;
    return escapeHatch
      ? { kind: "authorizedByForegroundLease" }
      : { kind: "authenticationUnavailable" };
  }

  return {
    kind: "requiresAuthentication",
    reason: input.requested
      ? JOURNAL_AUTH_REASONS.turnLockOn
      : JOURNAL_AUTH_REASONS.turnLockOff,
  };
}

export function resolveJournalLockChange(
  plan: JournalLockChangePlan,
  verified: boolean,
): JournalLockChangeOutcome {
  switch (plan.kind) {
    case "noChange":
      return { kind: "noChange" };
    case "authorizedByForegroundLease":
      return { kind: "authorizedByForegroundLease" };
    case "authenticationUnavailable":
      return { kind: "authenticationUnavailable" };
    case "requiresAuthentication":
      return verified
        ? { kind: "authorizedByAuthentication" }
        : { kind: "authenticationFailed" };
  }
}

/**
 * The toggle stays live while authentication is unavailable *only* for the
 * lease-driven turn-off, so the recovery path the copy describes is actually
 * reachable rather than being advice the user cannot follow.
 */
export function journalLockToggleEnabled(input: {
  readonly availability: JournalAuthenticationAvailability;
  readonly current: boolean;
  readonly isUnlocked: boolean;
  readonly hasVerifiedForegroundLease: boolean;
  readonly isChanging: boolean;
}): boolean {
  if (input.isChanging) return false;
  if (journalAuthenticationAvailable(input.availability)) return true;
  return input.current && input.isUnlocked && input.hasVerifiedForegroundLease;
}

// ---------------------------------------------------------------------------
// Storage: errors, loads, recovery
// ---------------------------------------------------------------------------

export type JournalRepositoryErrorCode =
  | "repositoryUnavailable"
  | "enumerationFailed"
  | "writeNotVerified"
  | "writeRollbackFailed"
  | "deletionNotVerified"
  | "recoveryRequired"
  | "revisionConflict"
  | "revisionOverflow"
  | "immutableContextChanged"
  | "attachmentMissing"
  | "attachmentCorrupt"
  | "attachmentTooLarge"
  | "attachmentImageTooLarge"
  | "unsafeRecoveryPath"
  | "unsafeRemovalPath"
  | "ioFailure";

/**
 * A single-item load never conflates "not there" with "cannot be read".
 * Collapsing the two is how an unreadable store gets silently replaced by an
 * empty one.
 */
export type JournalItemLoad<T> =
  | { readonly kind: "loaded"; readonly value: T }
  | { readonly kind: "missing" }
  | { readonly kind: "unavailable"; readonly error: JournalRepositoryErrorCode };

/**
 * A library read that could not complete carries the error alongside whatever
 * it did manage to read — never an empty array standing in for failure.
 */
export interface JournalLibraryRead<T> {
  readonly items: readonly T[];
  readonly recoveries: readonly JournalRecoveryItem[];
  readonly availabilityError?: JournalRepositoryErrorCode;
}

export type JournalRecoveryKind = "entry" | "draft" | "goal";

export const JOURNAL_RECOVERY_KIND_TITLES: Readonly<Record<JournalRecoveryKind, string>> =
  Object.freeze({ entry: "Entry", draft: "Draft", goal: "Goal" });

export type JournalRecoveryReason =
  | { readonly kind: "unreadable" }
  | { readonly kind: "futureSchema"; readonly found: number; readonly supported: number }
  | { readonly kind: "invalidIdentity" }
  | { readonly kind: "invalidContent" };

/**
 * A preserved file. It carries metadata only — never decoded content — because
 * the point of quarantine is that the content could not be trusted to decode.
 */
export interface JournalRecoveryItem {
  readonly id: string;
  readonly kind: JournalRecoveryKind;
  readonly reason: JournalRecoveryReason;
  /** Standardized path; used for the row key and re-verified before any export. */
  readonly path: string;
  readonly byteCount: number;
  readonly contentSHA256: Sha256Hex;
}

export function journalRecoveryPresentationId(
  item: JournalRecoveryItem,
): JournalRecoveryPresentationId {
  return `${item.kind}|${item.path}|${item.contentSHA256}` as JournalRecoveryPresentationId;
}

/** First eight characters, uppercased — enough to tell two rows apart. */
export function journalRecoveryShortId(id: string): string {
  return id.slice(0, 8).toUpperCase();
}

export function journalRecoveryRowTitle(item: JournalRecoveryItem): string {
  return `${JOURNAL_RECOVERY_KIND_TITLES[item.kind]} · ${journalRecoveryShortId(item.id)}`;
}

export function journalRecoveryExportName(item: JournalRecoveryItem): string {
  return `Journal recovery ${JOURNAL_RECOVERY_KIND_TITLES[item.kind]} ${journalRecoveryShortId(
    item.id,
  )}`;
}

export function journalRecoveryReasonText(reason: JournalRecoveryReason): string {
  switch (reason.kind) {
    case "unreadable":
      return JOURNAL_RECOVERY_COPY.unreadable;
    case "futureSchema":
      return `Created by journal format ${reason.found}; this version supports through ${reason.supported}.`;
    case "invalidIdentity":
      return JOURNAL_RECOVERY_COPY.invalidIdentity;
    case "invalidContent":
      return JOURNAL_RECOVERY_COPY.invalidContent;
  }
}

/** Filename sanitization for an exported copy; the raw path is never shared. */
export const JOURNAL_EXPORT_FILENAME_MAX_LENGTH = 72;
export const JOURNAL_EXPORT_EXTENSION_MAX_LENGTH = 12;
export const JOURNAL_EXPORT_FILENAME_FALLBACK = "Journal entry";
export const JOURNAL_EXPORT_EXTENSION_FALLBACK = "data";
export const JOURNAL_EXPORT_RETENTION: Milliseconds = 24 * 60 * 60 * 1_000;
export const JOURNAL_EXPORT_MAX_RETAINED = 16;

export function sanitizeJournalExportFilename(raw: string): string {
  const replaced = raw.replace(/[^A-Za-z0-9\-_ ]/g, "-").trim();
  const truncated = replaced.slice(0, JOURNAL_EXPORT_FILENAME_MAX_LENGTH).trim();
  return truncated.length > 0 ? truncated : JOURNAL_EXPORT_FILENAME_FALLBACK;
}

export function sanitizeJournalExportExtension(raw: string): string {
  const letters = raw.toLowerCase().replace(/[^a-z]/g, "");
  const truncated = letters.slice(0, JOURNAL_EXPORT_EXTENSION_MAX_LENGTH);
  return truncated.length > 0 ? truncated : JOURNAL_EXPORT_EXTENSION_FALLBACK;
}

// ---------------------------------------------------------------------------
// Storage: staging and cleanup constants
// ---------------------------------------------------------------------------

export const JOURNAL_MEDIA_STAGING = Object.freeze({
  staleAfter: 3_600_000 as Milliseconds,
  maxRetained: 12,
  storageReserveBytes: 32 * 1024 * 1024,
  copyChunkBytes: 1024 * 1024,
  filenamePrefix: "journal-pick-",
} as const);

export const JOURNAL_VOICE_STAGING = Object.freeze({
  staleAfter: 3_600_000 as Milliseconds,
  maxRetained: 4,
  storageReserveBytes: 32 * 1024 * 1024,
  filenamePrefix: "voice-recap-",
} as const);

/**
 * Import staging older than a day is abandoned; anything newer might be an
 * in-flight import lease, and deleting it would break a copy in progress.
 */
export const JOURNAL_IMPORT_STAGING_STALE_AFTER: Milliseconds = 24 * 60 * 60 * 1_000;

export const JOURNAL_MEDIA_PICKER_SELECTION_LIMIT = 6;

export interface JournalCleanupReport {
  readonly removedFiles: number;
  readonly removedBytes: number;
  readonly failedRemovals: number;
}

// ---------------------------------------------------------------------------
// Copy
// ---------------------------------------------------------------------------

export const JOURNAL_HOME_COPY = Object.freeze({
  navigationTitle: "Journal",
  displayTitle: "Keep the full picture.",
  subtitle:
    "Training, recovery, nutrition, and goals—turn each effort into something you can use.",
  writeButton: "Write",
  writeCaption: "Log your training",
  writeHint: "Opens a new journal entry",
  workspacePlaceholderTitle: "Corner Book",
  workspacePlaceholderBody:
    "Choose an entry to read it beside your timeline, or begin a new reflection.",
  newEntryButton: "New entry",
  searchPlaceholder: "Search your journal",
  searchPlaceholderAccessibility: "Search",
  searchAccessibilityLabel: "Search journal",
  continueWriting: "Continue writing",
  continueWritingHint: "Reopens the most recent autosaved draft",
  dateFilterTitle: "Choose a day",
  dateFilterField: "Entry date",
  dateFilterApply: "Show this day",
  dateFilterClear: "Clear date filter",
  promptCardLabel: "Training prompt",
  promptCardAnother: "Show another prompt",
  promptCardHide: "Hide this prompt",
} as const);

export const JOURNAL_LIBRARY_COPY = Object.freeze({
  loading: "Opening your protected journal…",
  unavailableTitle: "Journal is unavailable",
  unavailableFallback:
    "Fighting Form couldn't open the protected journal library. Existing writing has not been replaced.",
  retry: "Try again",
  stillOpening: "Your protected journal is still opening. Try again in a moment.",
  repositoryUnavailable:
    "Your protected journal is unavailable right now. Nothing was replaced.",
  enumerationFailed:
    "Your journal library could not be read safely. Nothing was replaced.",
  defaultUnavailable: "Your journal is unavailable right now. Nothing was replaced.",
  needsRecovery: "Some journal data needs recovery. It has not been overwritten.",
  cleanupPartial:
    "Unused journal media could not all be cleaned up. Your entries and drafts were left unchanged.",
  cleanupRecoveryRequired:
    "Journal cleanup paused because some data needs recovery. Nothing was overwritten.",
  cleanupOther:
    "Journal cleanup paused safely. Your entries, drafts, and media were left unchanged.",
} as const);

export const JOURNAL_RECOVERY_COPY = Object.freeze({
  sectionTitle: "Preserved for recovery",
  exportButton: "Verify & export preserved copy",
  exportingButton: "Verifying copy…",
  exportHint: "Checks the protected file and opens sharing only if its bytes still match",
  exportFailureTitle: "Recovery copy not exported",
  exportFailureBody:
    "Fighting Form could not verify this preserved file. Nothing was shared or changed.",
  unreadable: "The item could not be decoded. Its original bytes remain untouched.",
  invalidIdentity: "The stored identity did not match its protected file location.",
  invalidContent: "The content did not pass journal integrity checks.",
} as const);

export const JOURNAL_TIMELINE_COPY = Object.freeze({
  header: "Entries",
  loading: "Loading journal",
  noWrittenReflection: "No written reflection",
  draftBadge: "Draft",
  workoutRecapBadge: "Workout recap",
  linkedGoalBadge: "Linked goal",
  favorite: "Favorite",
  removeFavorite: "Remove favorite",
  resumeDraft: "Resume draft",
  delete: "Delete",
  entryOptions: "Entry options",
  favoriteEntryOptions: "Favorite entry options",
  openEntryHint: "Opens this entry",
  openDraftHint: "Opens the newest draft with an option to resume writing",
  showEveryDate: "Show every date",
} as const);

export const JOURNAL_EMPTY_STATE_COPY = Object.freeze({
  searchTitle: "Nothing matched",
  searchBody: "Try a different word, date, or lens.",
  dateTitle: "Nothing on this day",
  dateBody: "Choose another date or return to the full timeline.",
  favoritesTitle: "No favorites yet",
  defaultTitle: "Your journal starts here",
  defaultBody: "Review a workout, plan your training, or capture recovery and nutrition.",
} as const);

export interface JournalEmptyState {
  readonly title: string;
  readonly body?: string;
}

/**
 * Precedence matters: a search that matched nothing must not be explained as an
 * empty journal. Training/goals/personal deliberately fall through to the
 * default — those lenses are facets, not collections, so "no training entries"
 * would imply a container the user must fill.
 */
export function journalEmptyState(input: {
  readonly searchQuery: string;
  readonly hasDateFilter: boolean;
  readonly lens: JournalLens;
}): JournalEmptyState {
  if (input.searchQuery.trim().length > 0) {
    return {
      title: JOURNAL_EMPTY_STATE_COPY.searchTitle,
      body: JOURNAL_EMPTY_STATE_COPY.searchBody,
    };
  }
  if (input.hasDateFilter) {
    return {
      title: JOURNAL_EMPTY_STATE_COPY.dateTitle,
      body: JOURNAL_EMPTY_STATE_COPY.dateBody,
    };
  }
  if (input.lens === "favorites") {
    return { title: JOURNAL_EMPTY_STATE_COPY.favoritesTitle };
  }
  return {
    title: JOURNAL_EMPTY_STATE_COPY.defaultTitle,
    body: JOURNAL_EMPTY_STATE_COPY.defaultBody,
  };
}

export const JOURNAL_READER_COPY = Object.freeze({
  navigationTitle: "Entry",
  edit: "Edit this entry",
  resume: "Resume this draft",
  shareMarkdown: "Share Markdown…",
  shareHint: "Nothing leaves Fighting Form until you choose a share destination",
  deleteEntry: "Delete entry",
  savedGoalSnapshot: "Saved goal snapshot",
  perceivedEffortLabel: "Perceived effort",
  lastEditedPrefix: "Last edited",
  mediaUnavailable: "Media unavailable",
  concealedUntilRevealed: "Concealed until you reveal it",
  tapToView: "Tap to view",
} as const);

export const JOURNAL_DELETE_COPY = Object.freeze({
  entryDialogTitle: "Delete this journal entry?",
  entryConfirm: "Delete entry and its media",
  entryCancel: "Keep entry",
  entryFailureTitle: "Couldn't delete entry",
  entryFailureBody: "The entry wasn't deleted. It remains in your journal.",
  readerFailureTitle: "Entry unchanged",
  goalDialogTitle: "Delete this goal?",
  goalConfirm: "Delete goal",
  goalCancel: "Keep goal",
  goalBody: "Past journal entries keep their saved goal title for context.",
} as const);

export const JOURNAL_COMPOSER_COPY = Object.freeze({
  titlePlaceholder: "Title (optional)",
  bodyPlaceholder: "Record your workout, recovery, or nutrition notes…",
  bodyPlaceholderWithPrompt: "Use the prompt to make your next workout more informed.",
  closeAccessibilityLabel: "Close and keep draft",
  closeDialogTitle: "Close this entry?",
  closeDialogBody:
    "A kept draft stays available in Journal until you finish or discard it.",
  keepDraft: "Keep draft",
  discardChanges: "Discard changes",
  discardDraft: "Discard draft",
  keepWriting: "Keep writing",
  discardFailureTitle: "The draft is still here",
  discardFailureFallback: "Try again before closing the editor.",
  secureBeforeDiscardFailure:
    "The latest changes could not be secured before discard. Nothing was removed.",
  discardFailure: "The draft could not be discarded, so it was not closed.",
  workoutFactsFixed: "Workout facts are fixed",
  removePrompt: "Remove prompt",
  addMenu: "Add to entry",
  addMenuHint: "Entry details, photos, videos, and writing prompts",
  addEntryDetails: "Entry details",
  addPhotoOrVideo: "Photo or video",
  addWritingPrompt: "Writing prompt",
  recordVoiceRecap: "Record voice recap",
  retry: "Retry",
} as const);

export const JOURNAL_AUTOSAVE_COPY = Object.freeze({
  untouched: "Not saved yet",
  waiting: "Waiting to save",
  saving: "Saving…",
  saved: "Saved",
  failed: "Not saved",
  announcementPrefix: "Journal not saved.",
  revisionConflict: "This entry changed elsewhere. Your draft is still open.",
  recoveryRequired: "This entry needs recovery before it can be replaced.",
  attachmentUnverified: "A selected media file could not be verified.",
  defaultFailure: "Your latest changes are still on screen. Try saving again.",
  favoriteFailure: "That favorite change wasn't saved. Your entry is unchanged.",
} as const);

export const JOURNAL_RECAP_COPY = Object.freeze({
  refusedByDraft:
    "A journal draft already owns this workout's identity. It was left unchanged.",
  refusedByEntry:
    "A journal entry already owns this workout's identity. It was left unchanged.",
} as const);

export const JOURNAL_CONTEXT_COPY = Object.freeze({
  sheetTitle: "Context",
  entrySection: "Entry",
  typeLabel: "Type",
  momentLabel: "Moment",
  readinessSection: "Training readiness",
  readinessLabel: "Readiness",
  energyLabel: "Energy",
  goalsSection: "Goals and recall",
  goalNotLinked: "Not linked",
  tagsField: "Tags, separated by commas",
  createGoalToggle: "Create a training goal",
  hideGoalToggle: "Hide new goal",
  goalTitlePlaceholder: "What fitness or performance goal are you working toward?",
  goalWhyPlaceholder: "How will this improve your training or health? (optional)",
  addTargetDate: "Add a target date",
  createAndLink: "Create and link",
  goalSaveFailure: "That goal wasn't saved. Your entry is unchanged.",
  workoutRecapSection: "Workout recap",
  perceivedEffortLabel: "Perceived effort",
  bodyObservationPlaceholder:
    "Any pain, soreness, or movement limits during this workout?",
  footer:
    "Context is optional, user-authored, and stored with the protected entry on this device.",
} as const);

export const JOURNAL_PROMPT_PICKER_COPY = Object.freeze({
  title: "Choose a prompt",
  another: "Another",
  usePrompt: "Use prompt",
  footer:
    "Every prompt connects reflection to training, recovery, or fueling. Use one, replace it, or write your own workout note.",
} as const);

export const JOURNAL_MEDIA_COPY = Object.freeze({
  sheetTitle: "Media",
  question: "What are you adding?",
  purposeGeneral: "Memory",
  purposeProgressPhoto: "Progress photo",
  purposePerformanceVideo: "Performance video",
  purposeFormReference: "Form reference",
  progressPhotoNote: "Progress photos are concealed by default.",
  pickerLabel: "Choose photos or videos",
  pickerBusy: "Adding selected media…",
  unsupportedItem: "That item type isn't supported.",
  untransferableItem: "That item could not be transferred.",
  failedItem: "That item could not be added. Your entry is still here.",
  viewerTitle: "Media",
  concealedTitle: "Progress photo concealed",
  concealedBody:
    "Reveal it only when you're ready. It will be concealed again the next time you open it.",
  revealButton: "Reveal photo",
  loadFailure:
    "Fighting Form couldn't verify this media file. The journal entry is unchanged.",
  loadRetry: "Try again",
} as const);

export const JOURNAL_VOICE_COPY = Object.freeze({
  title: "Voice recap",
  privacyNote:
    "Recorded only while this screen is open. The finished clip stays protected with this journal entry on your device.",
  maxLabel: "10 min max",
  statusReady: "Ready",
  statusPermission: "Microphone access",
  statusRecording: "Recording",
  statusPaused: "Paused",
  statusReview: "Review",
  statusMicrophoneOff: "Microphone off",
  statusUnavailable: "Recording unavailable",
  detailReady: "Say what changed, what hurt, what worked, or what you want to remember.",
  detailPermission: "Fighting Form records only after you allow microphone access.",
  detailRecording:
    "The recording indicator and timer stay visible while the microphone is active.",
  detailPaused: "Nothing is being recorded. Resume or stop to review the clip.",
  detailReview: "Listen before attaching it. You can give the recording a useful title.",
  detailMicrophoneOff: "Written journaling remains fully available.",
  detailUnavailable: "Your written entry remains open and unchanged.",
  start: "Start recording",
  retryCleanup: "Retry cleanup",
  openSettings: "Open Settings",
  tryAgain: "Try again",
  waitingForPermission: "Waiting for microphone access…",
  pause: "Pause",
  stop: "Stop",
  resume: "Resume",
  review: "Review",
  rerecord: "Re-record",
  addToEntry: "Add to entry",
  adding: "Adding…",
  finishCleanup: "Finish cleanup",
  cleaning: "Cleaning…",
  titleField: "Recording title",
  positionLabel: "Voice recap position",
  discardDialogTitle: "Discard this recording?",
  discardDialogBody: "It has not been attached to the journal yet.",
  discardConfirm: "Discard recording",
  discardCancel: "Keep recording",
  notForeground: "Recording can start only while Fighting Form is active.",
  permissionDenied:
    "Microphone access is off. You can keep writing or enable it in Settings.",
  foregroundLost: "Recording did not start because Fighting Form was no longer active.",
  startFailedCleaned:
    "Fighting Form couldn't start this recording. Your written entry is unchanged.",
  startFailedStranded:
    "Recording did not start, and its temporary file still needs to be removed.",
  permissionAfterBackground:
    "Recording did not start because Fighting Form left the foreground.",
  resumeFailed:
    "The microphone couldn't resume. Review what was already recorded or try again.",
  verifyFailed: "That recording couldn't be verified, so it was not added.",
  verifyFailedStranded:
    "That recording couldn't be verified, and its temporary file still needs to be removed.",
  discardCleanupFailed:
    "The temporary recording could not be removed. Try discarding it again.",
  handoffCleanupFailed:
    "Your recap is saved, but its temporary recording still needs cleanup. Try again.",
  rerecordBlocked:
    "The old clip could not be removed, so a new recording was not started.",
  attachFailed: "The clip is still here. Try adding it again before closing.",
  attachHandoffFailed:
    "Your recap is saved. Retry temporary-file cleanup before closing.",
  retryCleanupFailed: "The temporary clip is still here. Try cleanup again.",
} as const);

export const JOURNAL_MEDIA_LIFECYCLE_COPY = Object.freeze({
  importCancelledUnverified:
    "The media import was cancelled, but its cleanup could not be verified.",
  importCancelledStranded:
    "The media import was cancelled, but its protected file could not be removed.",
  removalUnverified:
    "The entry is saved, but that media removal could not be verified.",
  removalFailed: "The entry is saved, but that media file could not be removed.",
} as const);

export const JOURNAL_GOALS_COPY = Object.freeze({
  sheetTitle: "Goals",
  heading: "Keep your training target close.",
  subheading:
    "Keep fitness and performance goals visible without turning them into another task list.",
  reflect: "Reflect",
  editDetails: "Edit details",
  deleteGoal: "Delete goal",
  emptyCurrentTitle: "What fitness result are you moving toward?",
  emptyCurrentBody:
    "Name a training, performance, recovery, or nutrition goal. You can reshape it later.",
  emptyOtherTitle: "No goals here",
  emptyOtherBody: "Choose another view or create a fitness goal.",
  failureTitle: "Goal unchanged",
  statusChangeFailure: "That status change wasn't saved.",
  deleteFailure: "The goal wasn't deleted.",
  detailNewTitle: "New goal",
  detailEditTitle: "Goal",
  aimSection: "Aim",
  whySection: "Why it matters",
  timingSection: "Timing",
  statusSection: "Status",
  titlePlaceholder: "What fitness or performance goal are you working toward?",
  whyPlaceholder: "How will this improve your training or health?",
  addTargetDate: "Add a target date",
  reflectOnGoal: "Reflect on this goal",
  discardDialogTitle: "Discard goal changes?",
  discardDialogBody: "Your unsaved goal changes will be lost.",
  discardConfirm: "Discard changes",
  discardCancel: "Keep editing",
  saveFailure: "That goal wasn't saved.",
  workspaceCard: "Goals workspace",
} as const);

export const JOURNAL_PRIVACY_COPY = Object.freeze({
  screenTitle: "Journal privacy",
  eyebrow: "PRIVATE WRITING",
  headline: "Your journal stays yours",
  body: "Journal content remains on this device. Protection and authentication add different layers of privacy.",
  protectionLabel: "Locked-device protection",
  protectionTrailing: "Always on",
  protectionBody:
    "Entries, working drafts, previews, and imported media use complete iOS file protection and cannot be read by Fighting Form while the device is locked.",
  lockLabel: "Journal Lock",
  lockOnDescription: "Require authentication",
  lockOffDescription: "Lock is off",
  lockOnResult: "Journal Lock is on.",
  lockOffResult: "Journal Lock is off. Locked-device file protection remains on.",
  unavailableWithLockOn:
    "Journal stays locked. Restore device authentication, then check again; Fighting Form will not expose it or turn the lock off automatically.",
  unavailableWithLockOff:
    "Journal Lock needs a device passcode before it can be turned on.",
  authenticationFailed: "Journal Lock wasn't changed because iOS did not verify you.",
  availableHintPrefix: "Use",
  availableHintSuffix: "before opening Journal after it has been concealed.",
  unavailableLockOnLease:
    "iOS authentication is unavailable, but this foreground session was already verified. You can safely turn the lock off now.",
  unavailableLockOnNoLease:
    "Journal remains locked. Restore device authentication in iOS Settings before opening it or turning the lock off.",
  unavailableLockOff:
    "Set a device passcode in iOS Settings before turning on Journal Lock.",
  recoveryLockOff:
    "Journal Lock needs device-owner authentication. Set a device passcode in iOS Settings, return here, then check again.",
  recoveryLockOnLease:
    "You already verified this uninterrupted foreground session, so you can safely turn Journal Lock off above. Leaving Fighting Form ends that recovery lease.",
  recoveryLockOnNoLease:
    "Fighting Form will never switch Journal Lock off automatically or expose entries when iOS cannot verify you. Set or restore a device passcode in iOS Settings, return here, then check again.",
  checkAgain: "Check again",
  openSettings: "Open iOS Settings",
  stillUnavailable: "Device authentication is still unavailable. Journal Lock was not changed.",
  nowAvailableSuffix: "is available. You can authenticate to change Journal Lock.",
  concealmentCovers:
    "Covers Journal immediately when Fighting Form leaves the foreground, whether Journal Lock is on or off.",
  concealmentVerifies:
    "Asks iOS to verify you. Fighting Form never receives biometric data.",
  concealmentScope:
    "Protects local access; it is not server-side or end-to-end encryption.",
  shieldAppName: "Fighting Form",
  shieldSubtitle: "Journal concealed",
  curtainTitle: "Journal concealed",
  curtainBodyLocked: "Authenticate with iOS to continue.",
  curtainBodyUnlocked: "Your writing is hidden while Fighting Form is away.",
  curtainBodyLockedDesktop: "Authenticate with macOS to continue.",
  curtainBodyUnlockedDesktop: "Your writing is hidden while Fighting Form is inactive.",
  unlock: "Unlock",
  continueWithoutUnlock: "Continue",
  notNow: "Not now",
} as const);

/** The curtain's primary action depends on whether anything is actually locked. */
export function journalCurtainAction(resume: JournalConcealedResume): string {
  return resume.kind === "requiresAuthentication"
    ? JOURNAL_PRIVACY_COPY.unlock
    : JOURNAL_PRIVACY_COPY.continueWithoutUnlock;
}

export function journalCurtainBody(
  resume: JournalConcealedResume,
  platform: "mobile" | "desktop" = "mobile",
): string {
  if (resume.kind === "requiresAuthentication") {
    return platform === "desktop"
      ? JOURNAL_PRIVACY_COPY.curtainBodyLockedDesktop
      : JOURNAL_PRIVACY_COPY.curtainBodyLocked;
  }
  return platform === "desktop"
    ? JOURNAL_PRIVACY_COPY.curtainBodyUnlockedDesktop
    : JOURNAL_PRIVACY_COPY.curtainBodyUnlocked;
}
